import {
  getLessonPrice,
  minLessonPrice,
  buildLessonBookingUrl,
  LESSON_PRICING,
  parseLessonCatalog,
  setLessonCatalog,
  getLessonCatalog,
  findLessonSlope,
  isLessonOptionEnabled,
  findLessonOption,
} from '../../../js/lessonPricing.js';
import { upsertDraft, getDraftForPage, removeDraft, setNamedField } from '../../../js/bookingDraft.js';
import { initStickySummaryBar } from '../../../js/stickySummaryBar.js';
import { getTurnstileConfig, mountTurnstile } from '../../../js/turnstile.js';
import { initExpandableLists } from '../../../js/expandableList.js';

const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const parseDate = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const formatIsoDate = (date) => {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const formatDisplayDate = (date) => {
  if (!date) return 'Wybierz termin';
  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

const formatDisplayDateFromIso = (iso) => {
  const date = parseDate(iso);
  return date ? formatDisplayDate(date) : '';
};

/** §9.2 / search: badge + breadcrumb z type[] (lekcje / instruktor dzieci|dorośli). */
const AUDIENCE_LABELS = new Set(['Dzieci', 'Dorośli', 'Dzieci 4-12 lat', 'Dzieci 4–12 lat']);

const isAudienceBadgeText = (text) => (
  AUDIENCE_LABELS.has(text)
  || /^dzieci(\s+4[-–]12\s+lat)?$/i.test(text)
  || /^dla dzieci/i.test(text)
  || /^doro[sś]li(\s*18\+)?$/i.test(text)
);

const isInstructorProductPage = (root) => {
  if (root?.querySelector?.('.js-instructor-lead-form, .js-instructor-lead-dialog')) return true;
  try {
    return /\/kurs-na-instruktora\/?$/i.test(window.location.pathname);
  } catch {
    return false;
  }
};

/** children|adults|'' z type[] (lekcje + kurs-instruktora / kurs-stopnie-instruktora). */
const resolveAudienceFromTypes = (types = []) => {
  let children = false;
  let adults = false;
  types.forEach((raw) => {
    const t = String(raw || '').toLowerCase();
    if (!t) return;
    if (t === 'kurs-instruktora') {
      children = true;
      return;
    }
    if (t === 'kurs-stopnie-instruktora') {
      adults = true;
      return;
    }
    if (t === 'lekcje-dzieci' || t === 'tyczki-dzieci' || t.includes('dzieci')) {
      children = true;
      return;
    }
    if (t === 'lekcje-dorosli' || t === 'tyczki-dorosli' || t === 'kursy-dorosli'
      || t.includes('dorosli') || t.includes('dorosł')) {
      adults = true;
    }
  });
  if (children && !adults) return 'children';
  if (adults && !children) return 'adults';
  return '';
};

const audienceFromReferrer = () => {
  try {
    const ref = String(document.referrer || '');
    if (/lista-szkolen-dzieci/i.test(ref)) return 'children';
    if (/lista-szkolen-dorosli/i.test(ref)) return 'adults';
  } catch {
    // ignore
  }
  return '';
};

/** destination[] slug → klucz lokalizacji (combo / booking `location=`). */
const DESTINATION_TO_LOCATION = {
  poland: 'harenda',
  'zakopane-harenda': 'harenda',
  harenda: 'harenda',
  gubalowka: 'gubalowka',
  nosal: 'nosal',
  italy: 'passo',
  'passo-del-tonale': 'passo',
  passo: 'passo',
  pellegrino: 'pellegrino',
  'san-pellegrino': 'pellegrino',
  'passo-san-pellegrino': 'pellegrino',
  livigno: 'livigno',
  kronplatz: 'kronplatz',
  austria: 'vorarlberg',
  vorarlberg: 'vorarlberg',
  salzburg: 'salzburg',
};

/** Klucz lokalizacji → etykieta obok pinu na stronie produktu kursu. */
const LOCATION_LABELS = {
  harenda: 'Harenda, Zakopane',
  gubalowka: 'Gubałówka',
  nosal: 'Nosal',
  passo: 'Passo del Tonale',
  pellegrino: 'San Pellegrino',
  livigno: 'Livigno',
  kronplatz: 'Kronplatz',
  vorarlberg: 'Vorarlberg',
  salzburg: 'Salzburg',
};

/** Booking location key → destination[] slug (listing / URL). */
const LOCATION_TO_DESTINATION = {
  harenda: 'zakopane-harenda',
  gubalowka: 'gubalowka',
  nosal: 'nosal',
  passo: 'passo-del-tonale',
  pellegrino: 'pellegrino',
  livigno: 'livigno',
  kronplatz: 'kronplatz',
  vorarlberg: 'vorarlberg',
  salzburg: 'salzburg',
};

const resolveLocationKey = (destinations = []) => {
  for (const dest of destinations) {
    if (DESTINATION_TO_LOCATION[dest]) return DESTINATION_TO_LOCATION[dest];
  }
  for (const dest of destinations) {
    if (LOCATION_LABELS[dest]) return dest;
  }
  return '';
};

const readProductSearchParams = () => {
  const params = new URLSearchParams(window.location.search);
  const types = params.getAll('type[]').concat(params.getAll('type')).filter(Boolean);
  const destinations = params.getAll('destination[]').concat(params.getAll('destination')).filter(Boolean);
  const disciplineRaw = String(params.get('discipline') || params.get('sport') || '').toLowerCase();
  const discipline = (disciplineRaw === 'snowboard' || disciplineRaw === 'sb' || disciplineRaw === 'board')
    ? 'snowboard'
    : 'ski';
  return {
    params,
    types,
    destinations,
    discipline,
    season: params.get('season') || '',
    dateFrom: params.get('date_from') || '',
    dateTo: params.get('date_to') || '',
    lessonDate: params.get('lesson_date') || '',
    adults: Number(params.get('adults') || 0),
    children: Number(params.get('children') || 0),
  };
};

/** Destynacje z URL albo z taxonomii strony (data-page-destinations). */
const effectiveDestinations = (root) => {
  const { destinations } = readProductSearchParams();
  if (destinations.length) return destinations;
  const raw = root?.dataset?.pageDestinations || '';
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
};

const schoolListUrl = (audience) => {
  const conf = window.hskiSearch || {};
  const raw = audience === 'children'
    ? (conf.schoolChildrenUrl || '/lista-szkolen-dzieci/')
    : (conf.schoolAdultsUrl || '/lista-szkolen-dorosli/');
  const fallback = audience === 'children' ? '/lista-szkolen-dzieci/' : '/lista-szkolen-dorosli/';
  try {
    return new URL(raw, window.location.origin).pathname.replace(/\/?$/, '/') || fallback;
  } catch {
    return fallback;
  }
};

const isPingwinProductPage = (root) => {
  if (root?.dataset?.productType === 'klub-pingwina') return true;
  if (root?.getAttribute?.('data-testid') === 'school-product-pingwin') return true;
  try {
    return /\/klub-pingwina\/?$/i.test(window.location.pathname);
  } catch {
    return false;
  }
};

/**
 * Pingwin: Dzieci › Klub Pingwina › Kurs X.
 * Inne produkty: tylko aktualizuje ostatni (current) crumb.
 */
const syncCourseBreadcrumb = (root, courseLabel) => {
  if (!root || !courseLabel) return;
  const list = root.querySelector('.offer-breadcrumb__list');
  if (!list) return;

  if (!isPingwinProductPage(root)) {
    list.querySelectorAll('.offer-breadcrumb__item--current').forEach((el) => {
      el.textContent = courseLabel;
    });
    return;
  }

  const childrenUrl = schoolListUrl('children');
  const hubUrl = '/klub-pingwina/';
  list.innerHTML = [
    `<li class="offer-breadcrumb__item"><a href="${escapeHtml(childrenUrl)}">Dzieci</a></li>`,
    `<li class="offer-breadcrumb__item"><a href="${escapeHtml(hubUrl)}">Klub Pingwina</a></li>`,
    `<li class="offer-breadcrumb__item offer-breadcrumb__item--current" aria-current="page">${escapeHtml(courseLabel)}</li>`,
  ].join('');
};

const badgePlainText = (el) => {
  const clone = el.cloneNode(true);
  clone.querySelectorAll('img, svg').forEach((node) => node.remove());
  return (clone.textContent || '').replace(/\s+/g, ' ').trim();
};

const setBadgeLabel = (el, label) => {
  const icon = el.querySelector('.school-product-page__badge-icon');
  el.replaceChildren();
  if (icon) el.appendChild(icon);
  el.appendChild(document.createTextNode(label));
};

const hydrateAudienceFromUrl = (root) => {
  const { types, params } = readProductSearchParams();
  const instructorPage = isInstructorProductPage(root);
  let audience = resolveAudienceFromTypes(types);
  if (!audience) {
    const q = String(params.get('audience') || '').toLowerCase();
    if (q === 'children' || q === 'dzieci') audience = 'children';
    else if (q === 'adults' || q === 'dorosli' || q === 'dorosły') audience = 'adults';
  }
  if (!audience && instructorPage) {
    audience = audienceFromReferrer() || 'adults';
  }
  if (!audience) return;

  const isChildren = audience === 'children';
  root.dataset.lessonAudience = isChildren ? 'children' : 'adults';
  root.dataset.instructorAudience = isChildren ? 'children' : 'adults';
  // Jak Klub Pingwina / SchoolProductRenderer::normalizeAudienceBadgeLabel
  const badge = isChildren ? 'Dzieci 4-12 lat' : 'Dorośli';
  const parentLabel = isChildren ? 'Dzieci' : 'Dorośli';
  const parentUrl = schoolListUrl(isChildren ? 'children' : 'adults');

  // Classic Twig miał badge--mobile + sidebar audience → po hydrate dwa „Dzieci”.
  // Gutenberg (SB) ma tylko badges--sidebar — trzymaj ten sam model.
  if (root.querySelector('.school-product-page__badges--sidebar .school-product-page__badge')) {
    root.querySelectorAll('.school-product-page__badge--mobile').forEach((el) => el.remove());
  }

  const badges = [...root.querySelectorAll('.school-product-page__badge')];
  const audienceBadges = badges.filter((el) => (
    el.dataset.role === 'audience' || isAudienceBadgeText(badgePlainText(el))
  ));
  const targets = audienceBadges.length ? audienceBadges : badges.slice(0, 1);
  targets.forEach((el, index) => {
    // Jeden badge audience (lekcje + kurs instruktora z dwoma tagami w CMS).
    if (index > 0) {
      el.remove();
      return;
    }
    setBadgeLabel(el, badge);
    el.dataset.role = 'audience';
  });

  root.querySelectorAll('.offer-breadcrumb__item a, .school-product-page__breadcrumb a').forEach((link) => {
    const text = (link.textContent || '').trim();
    const href = link.getAttribute('href') || '';
    if (text === 'Dzieci' || text === 'Dorośli' || /lista-szkolen-(dzieci|dorosli)/i.test(href)) {
      link.textContent = parentLabel;
      link.setAttribute('href', parentUrl);
    }
  });

  if (instructorPage) {
    filterInstructorAudiencePackages(root, isChildren ? 'children' : 'adults');
    syncInstructorLeadResumeUrl(root, isChildren ? 'children' : 'adults');
  }
};

/** Ukryj karty cennika z data-audience innym niż bieżąca grupa (gdy HTML ma obie). */
const filterInstructorAudiencePackages = (root, audience) => {
  root.querySelectorAll('.js-school-package-card[data-audience]').forEach((card) => {
    const cardAudience = String(card.dataset.audience || '').toLowerCase();
    if (!cardAudience || cardAudience === 'both') {
      card.hidden = false;
      return;
    }
    card.hidden = cardAudience !== audience;
  });
};

/** Zachowaj type[] w resume URL formularza lead. */
const syncInstructorLeadResumeUrl = (root, audience) => {
  const type = audience === 'children' ? 'kurs-instruktora' : 'kurs-stopnie-instruktora';
  root.querySelectorAll('.js-instructor-lead-form').forEach((form) => {
    form.dataset.audience = audience;
    const raw = form.dataset.resumeUrl || '/kurs-na-instruktora/#rezerwuj-miejsce';
    try {
      const url = new URL(raw, window.location.origin);
      url.searchParams.delete('type[]');
      url.searchParams.delete('type');
      url.searchParams.append('type[]', type);
      form.dataset.resumeUrl = `${url.pathname}${url.search}${url.hash || '#rezerwuj-miejsce'}`;
    } catch {
      form.dataset.resumeUrl = `/kurs-na-instruktora/?type[]=${encodeURIComponent(type)}#rezerwuj-miejsce`;
    }
  });
};

/** @deprecated alias — hydrateAudienceFromUrl */
const hydrateLessonAudience = hydrateAudienceFromUrl;

/** ski|snowboard z URL (discipline= / sport=); puste gdy brak parametru. */
const lessonDisciplineFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const raw = String(params.get('discipline') || params.get('sport') || '').toLowerCase();
  if (!raw) return '';
  return (raw === 'snowboard' || raw === 'sb' || raw === 'board') ? 'snowboard' : 'ski';
};

/** HTML radio: narty|snowboard */
const lessonSportRadioValue = (discipline = 'ski') => (
  discipline === 'snowboard' ? 'snowboard' : 'narty'
);

/**
 * Lekcje: zmiana Narty/Snowboard = przejście na właściwą stronę produktu (SEO).
 * URL-e z data-lesson-ski-url / data-lesson-snowboard-url (ustawienia Core).
 * Zachowuje type[] + destination[] (bez #kotwicy — start od góry).
 * Stok / osoby / godziny na tej samej stronie = history.replaceState (bez reloadu).
 */
const lessonDisciplinePagePath = (root, discipline = 'ski') => {
  const ski = String(root?.dataset?.lessonSkiUrl || '/lekcje-indywidualne-narty/').replace(/\/?$/, '/');
  const sb = String(root?.dataset?.lessonSnowboardUrl || '/lekcje-indywidualne-snowboard/').replace(/\/?$/, '/');
  return discipline === 'snowboard' ? sb : ski;
};

/** Strona produktu już niesie dyscyplinę (meta) — nie doklejaj discipline= (gorszy page-cache). */
const lessonPageImpliesDiscipline = (root) => {
  const raw = String(root?.dataset?.lessonDefaultDiscipline || '').toLowerCase();
  if (raw === 'snowboard' || raw === 'sb' || raw === 'board') return 'snowboard';
  if (raw === 'narty' || raw === 'ski') return 'ski';
  return '';
};

/** Path /lekcje-…-snowboard/ gdy brak meta (SSR/FE fallback). */
const lessonDisciplineFromPath = () => {
  try {
    const path = String(window.location.pathname || '').toLowerCase();
    if (path.includes('snowboard')) return 'snowboard';
  } catch {
    /* ignore */
  }
  return '';
};

/**
 * Dyscyplina do paczek / UI: URL → data-lesson-default-discipline → path → ski.
 */
const resolveLessonDiscipline = (root) => {
  const fromUrl = lessonDisciplineFromUrl();
  if (fromUrl) return fromUrl;
  const fromPage = lessonPageImpliesDiscipline(root);
  if (fromPage) return fromPage;
  const fromPath = lessonDisciplineFromPath();
  if (fromPath) return fromPath;
  const stored = String(root?.dataset?.lessonDiscipline || '').toLowerCase();
  if (stored === 'snowboard') return 'snowboard';
  return 'ski';
};

const LESSON_UI_TRANSFER_KEY = 'hski.lessonUiTransfer';
const LESSON_DISCIPLINE_SCROLL_TOP_KEY = 'hski-lesson-discipline-scroll-top';

/** Po nawigacji Narty↔Snowboard — bez #section-cennik, start od góry. */
const ensureLessonDisciplineScrollTop = () => {
  let shouldTop = false;
  try {
    shouldTop = sessionStorage.getItem(LESSON_DISCIPLINE_SCROLL_TOP_KEY) === '1';
    if (shouldTop) sessionStorage.removeItem(LESSON_DISCIPLINE_SCROLL_TOP_KEY);
  } catch {
    /* ignore */
  }
  if (!shouldTop && !/^#section-cennik$/i.test(window.location.hash || '')) return;
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  if (window.location.hash) {
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      history.replaceState({}, '', `${url.pathname}${url.search}`);
    } catch {
      /* ignore */
    }
  }
  window.scrollTo(0, 0);
};

const stashLessonUiForDisciplineNav = (root) => {
  const widget = liveLessonWidget(root);
  if (!widget) return;
  const state = getWidgetState(widget);
  const { types, destinations } = readProductSearchParams();
  try {
    sessionStorage.setItem(LESSON_UI_TRANSFER_KEY, JSON.stringify({
      ts: Date.now(),
      people: state.people,
      hours: state.hours,
      lessonType: state.lessonType,
      optId: state.optId || '',
      location: state.location || '',
      types,
      destinations,
      packagePicked: isLessonPackagePicked(root),
    }));
  } catch {
    /* private mode / quota */
  }
};

const consumeLessonUiTransfer = (root) => {
  let data = null;
  try {
    const raw = sessionStorage.getItem(LESSON_UI_TRANSFER_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(LESSON_UI_TRANSFER_KEY);
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  if (Date.now() - Number(data.ts || 0) > 90_000) return null;
  const widget = liveLessonWidget(root);
  if (!widget) return data;
  if (data.location) {
    const locCombo = widget.querySelector(
      '.js-school-lesson-location.js-school-combo, .js-school-combo.js-school-lesson-location',
    );
    const locOpt = [...(locCombo?.querySelectorAll('.js-school-combo-option') || [])].find(
      (opt) => opt.dataset.value === String(data.location),
    );
    if (locOpt) selectSchoolComboOption(locCombo, locOpt, { silent: true });
  }
  if (data.people) {
    const peopleCombo = widget.querySelector(
      '.js-school-lesson-people.js-school-combo, .js-school-combo.js-school-lesson-people',
    );
    const peopleOpt = [...(peopleCombo?.querySelectorAll('.js-school-combo-option') || [])].find(
      (opt) => opt.dataset.value === String(data.people),
    );
    if (peopleOpt) selectSchoolComboOption(peopleCombo, peopleOpt, { silent: true });
    else selectLessonPeople(widget, data.people, { force: true });
  }
  if (data.hours) {
    const hoursCombo = widget.querySelector(
      '.js-school-lesson-hours.js-school-combo, .js-school-combo.js-school-lesson-hours',
    );
    const hoursOpt = [...(hoursCombo?.querySelectorAll('.js-school-combo-option') || [])].find((opt) => (
      opt.dataset.value === String(data.hours)
      && (opt.dataset.lessonType || 'single') === (data.lessonType || 'single')
    ));
    if (hoursOpt) selectSchoolComboOption(hoursCombo, hoursOpt, { silent: true });
    else selectLessonHours(widget, data.hours, data.lessonType || 'single', { force: true });
  }
  if (data.packagePicked) setLessonPackagePicked(root, true);
  return data;
};

const navigateLessonDiscipline = (root, sportValue) => {
  const discipline = sportValue === 'snowboard' ? 'snowboard' : 'ski';
  const cur = new URL(window.location.href);
  // Tylko type[] + destination[] w URL (UI: sessionStorage). Bez #section-cennik.
  const nextParams = new URLSearchParams();
  const types = cur.searchParams.getAll('type[]').concat(cur.searchParams.getAll('type')).filter(Boolean);
  const destinations = cur.searchParams.getAll('destination[]')
    .concat(cur.searchParams.getAll('destination'))
    .filter(Boolean);
  types.forEach((t) => nextParams.append('type[]', t));
  destinations.forEach((d) => nextParams.append('destination[]', d));

  const targetPath = lessonDisciplinePagePath(root, discipline);
  const qs = nextParams.toString();
  const nextUrl = targetPath + (qs ? `?${qs}` : '');
  const curPath = cur.pathname.replace(/\/?$/, '/');
  const curCarry = new URLSearchParams();
  cur.searchParams.getAll('type[]').concat(cur.searchParams.getAll('type')).filter(Boolean)
    .forEach((t) => curCarry.append('type[]', t));
  cur.searchParams.getAll('destination[]').concat(cur.searchParams.getAll('destination')).filter(Boolean)
    .forEach((d) => curCarry.append('destination[]', d));
  const curUrlNoHash = `${curPath}${curCarry.toString() ? `?${curCarry}` : ''}`;
  if (nextUrl === curUrlNoHash || (targetPath === curPath && !qs && !curCarry.toString())) {
    syncSportBadge(root, sportValue);
    root.dataset.lessonDiscipline = discipline;
    return false;
  }
  // Ta sama ścieżka (legacy hub) — in-place + replaceState, bez reloadu / bez hasha.
  if (targetPath === curPath) {
    const nextLocal = cur.pathname + (qs ? `?${qs}` : '');
    if (nextLocal !== `${cur.pathname}${cur.search}${cur.hash}`) {
      history.replaceState({}, '', nextLocal);
    }
    syncSportBadge(root, sportValue);
    root.dataset.lessonDiscipline = discipline;
    window.scrollTo(0, 0);
    const widget = liveLessonWidget(root);
    const loc = widget ? String(getWidgetState(widget).location || '').trim() : '';
    if (loc) syncLessonLocationContent(root, loc);
    else if (typeof root._syncLessonWidget === 'function') root._syncLessonWidget();
    return false;
  }
  stashLessonUiForDisciplineNav(root);
  if (root) {
    try {
      showLeavingPageLoader(root);
    } catch {
      /* ignore */
    }
  }
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  try {
    sessionStorage.setItem(LESSON_DISCIPLINE_SCROLL_TOP_KEY, '1');
  } catch {
    /* ignore */
  }
  assignAfterPaint(nextUrl);
  return true;
};

/** Ustaw radio + badge z URL / meta strony po SSR / swapie hero. */
const hydrateLessonDisciplineFromUrl = (root) => {
  if (!root?.querySelector?.('.js-school-lesson-widget')) return;
  const discipline = resolveLessonDiscipline(root);
  const sportValue = lessonSportRadioValue(discipline);
  const input = queryProduct(root, `.school-product-page__sport-toggle input[name="sport"][value="${sportValue}"]`)
    || root.querySelector(`.school-product-page__sport-toggle input[name="sport"][value="${sportValue}"]`);
  if (input instanceof HTMLInputElement && !input.disabled) {
    input.checked = true;
  }
  syncSportBadge(root, sportValue);
  root.dataset.lessonDiscipline = discipline;
};

const lessonAudienceForBooking = (root) => {
  const fromData = String(root?.dataset?.lessonAudience || '').toLowerCase();
  if (fromData === 'children' || fromData === 'adults') return fromData;
  const { types } = readProductSearchParams();
  return resolveAudienceFromTypes(types);
};

/** Breadcrumb: Dzieci/Dorośli → listy szkoleń (nie unified /oferty/). */
const hydrateOfertyBreadcrumbs = (root) => {
  const childrenUrl = schoolListUrl('children');
  const adultsUrl = schoolListUrl('adults');
  root.querySelectorAll('.offer-breadcrumb__item a, .school-product-page__breadcrumb a').forEach((link) => {
    const text = (link.textContent || '').trim();
    const href = link.getAttribute('href') || '';
    if (text === 'Dzieci' || /lista-szkolen-dzieci/i.test(href)) {
      link.setAttribute('href', childrenUrl);
      return;
    }
    if (text === 'Dorośli' || /lista-szkolen-dorosli/i.test(href)) {
      link.setAttribute('href', adultsUrl);
    }
  });
};

/** Daty z wyszukiwarki → kalendarz Termin (date_from / lesson_date). */
const hydrateLessonDateFromUrl = (widget) => {
  const { lessonDate, dateFrom, dateTo } = readProductSearchParams();
  const iso = lessonDate || dateFrom || '';
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;

  const input = widget.querySelector('.js-school-lesson-date');
  const display = widget.querySelector('.js-school-lesson-date-display');
  if (input) input.value = iso;
  if (display) display.textContent = formatDisplayDateFromIso(iso);

  // Zakres pobytu (date_to) — informacyjnie w data-* (CRM/grafik później).
  if (dateTo && dateTo !== iso) {
    widget.dataset.stayDateTo = dateTo;
  }
};

const hydrateLessonLocationFromUrl = (widget, root) => {
  const destinations = effectiveDestinations(root || widget?.closest('.js-school-product'));
  if (!destinations.length) return;

  const locationCombo = widget.querySelector('.js-school-lesson-location');
  if (!locationCombo) return;

  for (const dest of destinations) {
    const location = DESTINATION_TO_LOCATION[dest] || dest;
    const match = [...locationCombo.querySelectorAll('.js-school-combo-option')].find(
      (opt) => (
        opt.dataset.value === location
        || opt.dataset.destination === dest
        || opt.dataset.destination === location
      ),
    );
    if (match) {
      selectSchoolComboOption(locationCombo, match);
      return;
    }
  }
};

/** Kurs grupowy: select stoku albo legacy pin z destination[] / tax strony. */
const hydrateCourseLocationFromUrl = (root) => {
  const destinations = effectiveDestinations(root);
  const locationCombo = root.querySelector('.js-school-course-location');
  const locationKey = resolveLocationKey(destinations) || root.dataset.location || '';

  if (locationCombo) {
    const match = [...locationCombo.querySelectorAll('.js-school-combo-option')].find((opt) => (
      (locationKey && opt.dataset.value === locationKey)
      || destinations.some((dest) => opt.dataset.destination === dest || opt.dataset.value === dest)
    ));
    if (match) selectSchoolComboOption(locationCombo, match);
    root.dataset.location = locationCombo.dataset.value || locationKey || '';
    return;
  }

  if (!destinations.length) return;

  const label = LOCATION_LABELS[locationKey];
  if (!locationKey || !label) return;

  root.dataset.location = locationKey;
  root.querySelectorAll('.school-product-page__location').forEach((el) => {
    const svg = el.querySelector('svg');
    el.replaceChildren();
    if (svg) el.appendChild(svg);
    el.appendChild(document.createTextNode(label));
  });
};

const applyCourseLocationChange = (root, combo) => {
  if (!combo?.classList.contains('js-school-course-location')) return false;

  const value = combo.dataset.value || '';
  const option = [...combo.querySelectorAll('.js-school-combo-option')].find(
    (opt) => opt.dataset.value === value,
  );
  const destination = option?.dataset.destination
    || LOCATION_TO_DESTINATION[value]
    || value;
  const productUrl = (option?.dataset.productUrl || '').trim();

  root.dataset.location = value;

  const cur = new URL(window.location.href);
  const nextParams = new URLSearchParams(cur.search);
  nextParams.delete('destination[]');
  nextParams.delete('destination');
  if (destination) nextParams.append('destination[]', destination);

  if (productUrl) {
    try {
      const next = new URL(productUrl, window.location.origin);
      const curPath = cur.pathname.replace(/\/$/, '') || '/';
      const nextPath = next.pathname.replace(/\/$/, '') || '/';
      if (nextPath !== curPath) {
        const qs = nextParams.toString();
        try {
          showLeavingPageLoader(root);
        } catch {
          /* ignore */
        }
        assignAfterPaint(nextPath + (qs ? `?${qs}` : ''));
        return true;
      }
    } catch {
      /* stay on page */
    }
  }

  const qs = nextParams.toString();
  const nextUrl = cur.pathname + (qs ? `?${qs}` : '') + cur.hash;
  if (nextUrl !== `${cur.pathname}${cur.search}${cur.hash}`) {
    history.replaceState({}, '', nextUrl);
  }
  return false;
};

const hydrateLessonPeopleFromUrl = (widget) => {
  const { adults, children } = readProductSearchParams();
  const total = adults + children;
  if (total < 1) return;

  const people = String(Math.min(3, Math.max(1, total)));
  const peopleCombo = widget.querySelector('.js-school-lesson-people');
  if (!peopleCombo) return;

  const match = [...peopleCombo.querySelectorAll('.js-school-combo-option')].find(
    (opt) => opt.dataset.value === people,
  );
  if (match) selectSchoolComboOption(peopleCombo, match);
};

const hydrateProductSearchMeta = (root) => {
  const { season, destinations, types } = readProductSearchParams();
  const pageDestinations = effectiveDestinations(root);
  if (season) root.dataset.searchSeason = season;
  if (destinations.length) {
    root.dataset.searchDestinations = destinations.join(',');
  } else if (pageDestinations.length) {
    root.dataset.searchDestinations = pageDestinations.join(',');
  }
  if (types.length) root.dataset.searchTypes = types.join(',');
  hydrateOfertyBreadcrumbs(root);
};

const hydrateInstructorFromUrl = (root) => {
  const { types, dateFrom, dateTo } = readProductSearchParams();
  const forms = root.querySelectorAll('.js-instructor-lead-form');
  if (!forms.length) return;

  forms.forEach((form) => {
    const courseTypeCombo = form.querySelector('[data-combo="course_type"]');
    if (courseTypeCombo && types.length) {
      const match = [...courseTypeCombo.querySelectorAll('.js-school-combo-option')].find((opt) => (
        types.includes(opt.dataset.value)
        || types.some((t) => (opt.dataset.value || '').includes(t) || t.includes(opt.dataset.value || ''))
      ));
      if (match) selectSchoolComboOption(courseTypeCombo, match);
    }

    const termCombo = form.querySelector('[data-combo="term"]');
    if (termCombo && dateFrom && dateTo) {
      const match = [...termCombo.querySelectorAll('.js-school-combo-option')].find((opt) => (
        opt.dataset.value === `${dateFrom}_${dateTo}`
        || (opt.dataset.dateFrom === dateFrom && opt.dataset.dateTo === dateTo)
      ));
      if (match) selectSchoolComboOption(termCombo, match);
    }
  });
};

const syncSportBadge = (root, sportValue) => {
  const label = sportValue === 'snowboard' ? 'Snowboard' : 'Narty';
  const badges = [...root.querySelectorAll('.school-product-page__badge')];
  const sportBadges = badges.filter((el) => (
    el.dataset.role === 'sport'
    || /^(narty|snowboard)$/i.test(badgePlainText(el))
  ));
  sportBadges.forEach((el) => {
    setBadgeLabel(el, label);
    el.dataset.role = 'sport';
  });
};

const getExtraMonthKey = (monthKey) => {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(year, month, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const initLessonDatePicker = (widget, onChange) => {
  const picker = widget.querySelector('.js-school-lesson-date-picker');
  if (!picker || picker.dataset.dateBound === '1') return;
  picker.dataset.dateBound = '1';

  const input = picker.querySelector('.js-school-lesson-date');
  const trigger = picker.querySelector('.js-school-lesson-date-trigger');
  const display = picker.querySelector('.js-school-lesson-date-display');
  const popover = picker.querySelector('.js-school-lesson-date-popover');
  const grid = picker.querySelector('.js-school-lesson-calendar-grid');
  const title = picker.querySelector('.js-school-lesson-calendar-title');
  const extraGrid = picker.querySelector('.js-school-lesson-calendar-grid-extra');
  const extraTitle = picker.querySelector('.js-school-lesson-calendar-title-extra');
  if (!input || !trigger || !display || !popover || !grid || !title) return;

  const state = {
    selected: parseDate(input.value) || new Date(),
    viewMonth: (input.value || formatIsoDate(new Date())).slice(0, 7),
  };

  const syncDisplay = () => {
    display.textContent = formatDisplayDate(state.selected);
    input.value = formatIsoDate(state.selected);
  };

  const closePopover = () => {
    picker.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    popover.hidden = true;
  };

  const openPopover = () => {
    picker.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    popover.hidden = false;
    renderCalendar();
  };

  const renderMonth = (targetGrid, targetTitle, monthKey, interactive = true) => {
    if (!targetGrid) return;

    const [year, month] = monthKey.split('-').map(Number);
    if (targetTitle) targetTitle.textContent = `${MONTH_NAMES[month - 1]} ${year}`;
    targetGrid.innerHTML = '';

    const firstDay = new Date(year, month - 1, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month, 0).getDate();
    const prevMonthDays = new Date(year, month - 1, 0).getDate();

    for (let i = 0; i < startOffset; i += 1) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'school-product-page__calendar-day school-product-page__calendar-day--muted';
      btn.textContent = String(prevMonthDays - startOffset + i + 1);
      btn.tabIndex = -1;
      targetGrid.appendChild(btn);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month - 1, day);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'school-product-page__calendar-day';
      btn.textContent = String(day);
      btn.dataset.date = formatIsoDate(date);

      if (state.selected && date.getTime() === state.selected.getTime()) {
        btn.classList.add('school-product-page__calendar-day--selected');
      }

      if (interactive) {
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          state.selected = parseDate(btn.dataset.date);
          syncDisplay();
          renderCalendar();
          closePopover();
          input.dispatchEvent(new Event('change', { bubbles: true }));
          onChange?.();
        });
      } else {
        btn.tabIndex = -1;
      }

      targetGrid.appendChild(btn);
    }
  };

  const renderCalendar = () => {
    renderMonth(grid, title, state.viewMonth, true);
    renderMonth(extraGrid, extraTitle, getExtraMonthKey(state.viewMonth), true);
  };

  trigger.addEventListener('click', (event) => {
    event.stopPropagation();
    if (picker.classList.contains('is-open')) {
      closePopover();
    } else {
      if (state.selected) state.viewMonth = formatIsoDate(state.selected).slice(0, 7);
      openPopover();
    }
  });

  picker.querySelector('.js-school-lesson-calendar-prev')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const [year, month] = state.viewMonth.split('-').map(Number);
    const date = new Date(year, month - 2, 1);
    state.viewMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    renderCalendar();
  });

  picker.querySelector('.js-school-lesson-calendar-next')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const [year, month] = state.viewMonth.split('-').map(Number);
    const date = new Date(year, month, 1);
    state.viewMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    renderCalendar();
  });

  popover.addEventListener('click', (event) => event.stopPropagation());

  document.addEventListener('click', (event) => {
    if (!picker.contains(event.target)) closePopover();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePopover();
  });

  syncDisplay();
  renderCalendar();
};

const formatPrice = (value) => `${value.toLocaleString('pl-PL')} zł`;

const peopleLabel = (count) => {
  const n = Number(count);
  if (n === 1) return 'Lekcje dla 1 osoby';
  return `Lekcje dla ${n} osób`;
};

const hoursLabel = (hours, lessonType) => {
  const n = Number(hours) || 0;
  if (lessonType === 'season') return `Pakiet ${n} godzin`;
  if (n === 1) return '1 godzina';
  if (n > 1 && n < 5) return `${n} godziny`;
  return `${n} godzin`;
};

const perPersonLabel = (total, people) => {
  const n = Number(people);
  if (n <= 1) return '';
  const per = total / n;
  return `koszt ${per.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł / osobę`;
};

const peopleCountLabel = (count) => {
  const n = Number(count);
  if (n === 1) return '1 osobę';
  return `${n} osoby`;
};

const priceForTemplate = (root) => {
  const fromSection = root?.querySelector?.('.js-school-packages')?.dataset?.priceForTemplate;
  const raw = fromSection || root?.dataset?.priceForTemplate || '';
  return String(raw || 'łącznie za {people} do jednego instruktora').trim();
};

const formatPriceForLabel = (people, template) => {
  const tpl = String(template || '').trim();
  if (!tpl) return '';
  return tpl.replaceAll('{people}', peopleCountLabel(people));
};

const movePackageTabIndicator = (tabs, btn) => {
  const indicator = tabs?.querySelector('.js-school-package-tab-indicator');
  if (!indicator) return;
  if (!btn) {
    indicator.style.width = '0px';
    indicator.style.left = '4px';
    return;
  }
  const inset = 4; // przerwa od szarych borderów między 1/2/3
  const tabsRect = tabs.getBoundingClientRect();
  const btnRect = btn.getBoundingClientRect();
  indicator.style.width = `${Math.max(0, btnRect.width - inset * 2)}px`;
  indicator.style.left = `${btnRect.left - tabsRect.left + inset}px`;
  indicator.style.transform = '';
};

const getWidgetState = (widget) => {
  const hoursCombo = widget.querySelector('.js-school-lesson-hours.js-school-combo, .js-school-combo.js-school-lesson-hours');
  const locationCombo = widget.querySelector('.js-school-lesson-location.js-school-combo, .js-school-combo.js-school-lesson-location');
  const peopleCombo = widget.querySelector('.js-school-lesson-people.js-school-combo, .js-school-combo.js-school-lesson-people');
  // Legacy native selects (fallback)
  const hoursSelect = widget.querySelector('select.js-school-lesson-hours');
  const locationSelect = widget.querySelector('select.js-school-lesson-location');
  const peopleSelect = widget.querySelector('select.js-school-lesson-people');

  return {
    discipline: widget.querySelector('[name="sport"]:checked')?.value === 'snowboard' ? 'snowboard' : 'ski',
    location: locationCombo?.dataset.value || locationSelect?.value || 'harenda',
    people: Number(peopleCombo?.dataset.value || peopleSelect?.value || 1),
    hours: Number(hoursCombo?.dataset.value || hoursSelect?.value || 1),
    lessonType: hoursCombo?.dataset.lessonType || hoursSelect?.selectedOptions?.[0]?.dataset.lessonType || 'single',
    optId: hoursCombo?.dataset.optId || hoursSelect?.selectedOptions?.[0]?.dataset.optId || '',
  };
};

const applyLessonCatalogFilters = (root, widget) => {
  const state = getWidgetState(widget);
  const catalog = getLessonCatalog();
  const catalogSlopes = Array.isArray(catalog?.slopes) ? catalog.slopes : [];
  const allowedLocations = new Set(
    catalogSlopes.map((slope) => String(slope?.id || '')).filter(Boolean),
  );

  const locationCombo = widget.querySelector(
    '.js-school-lesson-location.js-school-combo, .js-school-combo.js-school-lesson-location',
  );
  if (locationCombo && allowedLocations.size > 0) {
    let firstLoc = null;
    let currentLocVisible = false;
    [...locationCombo.querySelectorAll('.js-school-combo-option')].forEach((opt) => {
      const value = String(opt.dataset.value || '');
      const show = allowedLocations.has(value);
      opt.hidden = !show;
      if (show && !firstLoc) firstLoc = opt;
      if (show && value === String(state.location || '')) currentLocVisible = true;
    });
    if (!currentLocVisible && firstLoc) {
      selectSchoolComboOption(locationCombo, firstLoc, { silent: true });
    }
  }

  const locationSelect = widget.querySelector('select.js-school-lesson-location');
  if (locationSelect && allowedLocations.size > 0) {
    [...locationSelect.options].forEach((opt) => {
      const show = allowedLocations.has(String(opt.value || ''));
      opt.hidden = !show;
      opt.disabled = !show;
    });
    const selected = locationSelect.selectedOptions[0];
    if (selected?.hidden) {
      const next = [...locationSelect.options].find((opt) => !opt.hidden);
      if (next) locationSelect.value = next.value;
    }
  }

  const nextState = getWidgetState(widget);
  syncLessonDisciplines(root, nextState.location);

  const hoursCombo = widget.querySelector('.js-school-lesson-hours.js-school-combo, .js-school-combo.js-school-lesson-hours');
  const hoursSelect = widget.querySelector('select.js-school-lesson-hours');
  let firstVisible = null;
  let currentVisible = false;

  const liveState = getWidgetState(widget);
  const optionEnabled = (hours, lessonType, optId) => {
    const option = findLessonOption({ hours, lessonType, optId });
    return isLessonOptionEnabled(liveState.location, optId || option?.id || '');
  };

  (hoursCombo ? [...hoursCombo.querySelectorAll('.js-school-combo-option')] : []).forEach((opt) => {
    const hours = Number(opt.dataset.value);
    const lessonType = opt.dataset.lessonType || 'single';
    const enabled = optionEnabled(hours, lessonType, opt.dataset.optId || '');
    opt.hidden = !enabled;
    if (enabled && !firstVisible) firstVisible = opt;
    if (
      enabled
      && opt.dataset.value === String(state.hours)
      && (opt.dataset.lessonType || 'single') === (state.lessonType || 'single')
    ) {
      currentVisible = true;
    }
  });
  if (hoursCombo && !currentVisible && firstVisible) {
    selectSchoolComboOption(hoursCombo, firstVisible, { silent: true });
  }

  if (hoursSelect) {
    [...hoursSelect.options].forEach((opt) => {
      const enabled = optionEnabled(
        Number(opt.value),
        opt.dataset.lessonType || 'single',
        opt.dataset.optId || '',
      );
      opt.hidden = !enabled;
      opt.disabled = !enabled;
    });
    const selected = hoursSelect.selectedOptions[0];
    if (selected?.hidden) {
      const next = [...hoursSelect.options].find((opt) => !opt.hidden);
      if (next) hoursSelect.value = next.value;
    }
  }

  root.querySelectorAll('.js-school-price-card').forEach((card) => {
    const enabled = optionEnabled(
      Number(card.dataset.hours),
      card.dataset.lessonType || 'single',
      card.dataset.optId || '',
    );
    card.hidden = !enabled;
  });
};

const isLessonPackagePicked = (root) => root?.dataset?.packageSelected === '1';

const setLessonPackagePicked = (root, picked) => {
  if (!root) return;
  root.dataset.packageSelected = picked ? '1' : '0';
};

const syncPriceCards = (root, state, bookingPath) => {
  const people = Number(state.people) || 1;
  const picked = isLessonPackagePicked(root);
  const forTemplate = priceForTemplate(root);
  const forLabel = formatPriceForLabel(people, forTemplate);
  root.querySelectorAll('.js-school-price-card').forEach((card) => {
    const hours = Number(card.dataset.hours);
    const lessonType = card.dataset.lessonType || 'single';
    const price = getLessonPrice({ people, hours, lessonType, location: state.location, optId: card.dataset.optId || '', discipline: state.discipline });
    const selected = picked
      && Number(state.hours) === hours
      && (state.lessonType || 'single') === lessonType;

    card.classList.toggle('is-selected', selected);
    if (card.hidden) return;

    const valueEl = card.querySelector('.js-school-price-value');
    if (valueEl) {
      const suffix = people > 1 ? ` <small>/ ${peopleCountLabel(people)}</small>` : '';
      valueEl.innerHTML = `${formatPrice(price)}${suffix}`;
    }

    const forEl = card.querySelector('.js-school-price-for');
    if (forEl) {
      forEl.textContent = forLabel;
      forEl.hidden = !forLabel;
    }

    const perEl = card.querySelector('.js-school-price-per');
    if (perEl) {
      const per = perPersonLabel(price, people);
      perEl.textContent = per;
      perEl.hidden = !per;
    }

    const tabs = card.querySelector('.js-school-package-tabs');
    const buttons = tabs ? [...tabs.querySelectorAll('[data-people]')] : [];
    let activeBtn = null;
    buttons.forEach((btn) => {
      // Taby osób + biały indicator tylko na wybranym pakiecie (nie na wszystkich kartach na starcie).
      const on = selected && btn.dataset.people === String(people);
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
      if (on) activeBtn = btn;
    });
    if (tabs) movePackageTabIndicator(tabs, activeBtn);
  });
};

const initLessonWidget = (root, _initialWidget) => {
  const bookingPath = root.dataset.bookingPath || '/rezerwacja-lekcji/';

  // Sidebar ma domyślnie 1 os. / 1 h — od razu zaznacz ten sam pakiet na kartach
  // (is-selected / tab is-active). Wcześniej „jak Pingwin” startowało bez wyboru,
  // więc UI pakietów wyglądało na niewybrane mimo wartości w combo.
  if (root.dataset.packageSelected == null || root.dataset.packageSelected === '') {
    setLessonPackagePicked(root, true);
  }

  const syncLessonSidebarCta = (widget, state, picked) => {
    const ctaLink = queryProduct(root, '.js-school-lesson-cta')
      || widget?.querySelector?.('.js-school-lesson-cta');
    if (!ctaLink) return;
    // Jedna etykieta z Klubem Pingwina — niezależnie od SSR / Carbon („Zamów lekcje”).
    ctaLink.dataset.bookingLabel = 'Zarezerwuj teraz';
    if (picked) {
      ctaLink.href = buildLessonBookingUrl({
        lessonType: state.lessonType,
        people: state.people,
        hours: state.hours,
        discipline: state.discipline,
        location: state.location,
        optId: state.optId || '',
        audience: lessonAudienceForBooking(root),
      }, bookingPath);
      ctaLink.textContent = 'Zarezerwuj teraz';
      ctaLink.classList.remove('is-muted', 'is-pick-package');
      ctaLink.removeAttribute('aria-disabled');
    } else {
      ctaLink.href = '#';
      ctaLink.textContent = 'Wybierz pakiet';
      ctaLink.classList.add('is-muted', 'is-pick-package');
      ctaLink.setAttribute('aria-disabled', 'true');
    }
  };

  const syncWidget = () => {
    const widget = liveLessonWidget(root);
    if (!widget) return;
    applyLessonCatalogFilters(root, widget);
    const state = getWidgetState(widget);
    const picked = isLessonPackagePicked(root);
    const price = picked
      ? getLessonPrice(state)
      : minLessonPrice({ people: state.people, location: state.location });

    const priceEl = widget.querySelector('.js-school-lesson-price');
    const peopleLabelEl = widget.querySelector('.js-school-lesson-people-label');
    const hoursLabelEl = widget.querySelector('.js-school-lesson-hours-label');

    if (priceEl) {
      priceEl.textContent = formatPrice(price);
    }
    if (peopleLabelEl) peopleLabelEl.textContent = peopleLabel(state.people);
    if (hoursLabelEl) hoursLabelEl.textContent = hoursLabel(state.hours, state.lessonType);
    syncLessonSidebarCta(widget, state, picked);
    syncPriceCards(root, state, bookingPath);
    root._stickySummaryApi?.sync?.();
  };

  let lastLessonLocation = String(getWidgetState(liveLessonWidget(root) || _initialWidget).location || '');

  /** Same-page: destination[] / adults|children przez history — bez pełnego reloadu. */
  const replaceLessonSearchParams = (mutate) => {
    const cur = new URL(window.location.href);
    const nextParams = new URLSearchParams(cur.search);
    mutate(nextParams);
    // Dyscyplina = strona SEO; nie doklejaj discipline= gdy meta strony już ją niesie.
    nextParams.delete('sport');
    const pageDisc = lessonPageImpliesDiscipline(root);
    const widget = liveLessonWidget(root);
    const uiDisc = widget?.querySelector?.('[name="sport"]:checked')?.value === 'snowboard'
      ? 'snowboard'
      : 'ski';
    const discipline = lessonDisciplineFromUrl() || uiDisc;
    if (discipline === 'snowboard' && pageDisc !== 'snowboard') {
      nextParams.set('discipline', 'snowboard');
    } else {
      nextParams.delete('discipline');
    }
    const qs = nextParams.toString();
    // Bez hash — unikaj skoku do #section-cennik przy syncu opcji.
    const nextUrl = cur.pathname + (qs ? `?${qs}` : '');
    const curNoHash = `${cur.pathname}${cur.search}`;
    if (nextUrl !== curNoHash) {
      history.replaceState({}, '', nextUrl);
    }
  };

  const syncLessonLocationUrl = (locationValue) => {
    const value = String(locationValue || '').trim();
    if (!value) return;
    const widget = liveLessonWidget(root);
    const combo = widget?.querySelector?.('.js-school-combo.js-school-lesson-location');
    const option = [...(combo?.querySelectorAll('.js-school-combo-option') || [])].find(
      (opt) => opt.dataset.value === value,
    );
    const destination = option?.dataset.destination
      || LOCATION_TO_DESTINATION[value]
      || value;
    root.dataset.location = value;
    replaceLessonSearchParams((nextParams) => {
      nextParams.delete('destination[]');
      nextParams.delete('destination');
      nextParams.delete('location');
      if (destination) nextParams.append('destination[]', destination);
    });
  };

  const syncLessonPeopleHoursUrl = () => {
    const widget = liveLessonWidget(root);
    if (!widget) return;
    const state = getWidgetState(widget);
    const people = Math.min(3, Math.max(1, Number(state.people) || 1));
    const audience = lessonAudienceForBooking(root);
    replaceLessonSearchParams((nextParams) => {
      nextParams.delete('adults');
      nextParams.delete('children');
      if (audience === 'children') {
        nextParams.set('children', String(people));
        nextParams.set('adults', '0');
      } else {
        nextParams.set('adults', String(people));
        nextParams.delete('children');
      }
    });
  };

  const syncLessonLocationIfChanged = () => {
    const widget = liveLessonWidget(root);
    if (!widget) return;
    const loc = String(getWidgetState(widget).location || '').trim();
    if (!loc || loc === lastLessonLocation) return;
    lastLessonLocation = loc;
    syncLessonLocationUrl(loc);
    // Overlay tylko gdy paczki jeszcze nie ma w pamięci (kolejne stoki = instant).
    const packs = parseSchoolCourseContentPacks(root);
    const contentKey = lessonContentKeyForRoot(root);
    const cached = slopeHasLessonPack(packs, loc, contentKey);
    syncLessonLocationContent(root, loc, { withLoading: !cached });
  };

  // Listenery raz na root — hero/sidebar ginie przy swapie paczki.
  if (root.dataset.lessonWidgetBound !== '1') {
    root.dataset.lessonWidgetBound = '1';

    // Native selects (legacy) — delegacja, bo węzeł też może być w nowym hero.
    root.addEventListener('change', (event) => {
      const el = event.target;
      if (!(el instanceof HTMLElement)) return;
      const widget = liveLessonWidget(root);
      if (!widget || (!widget.contains(el) && !productSidebarDrawer(root)?.contains(el))) return;
      if (el.matches?.('select.js-school-lesson-people, select.js-school-lesson-hours')) {
        setLessonPackagePicked(root, true);
        syncLessonPeopleHoursUrl();
      }
      if (el.matches?.('select.js-school-lesson-location')) {
        syncLessonLocationIfChanged();
      }
      if (el.matches?.('[name="sport"]')) {
        // Lekcje: przejście na stronę drugiej dyscypliny (SEO). Kursy: in-place.
        if (root.querySelector('.js-school-lesson-widget') && navigateLessonDiscipline(root, el.value)) {
          return;
        }
        syncSportBadge(root, el.value);
      }
      if (el.matches?.(
        '[name="sport"], select.js-school-lesson-location, select.js-school-lesson-people, select.js-school-lesson-hours',
      )) {
        syncWidget();
      }
    });

    // Mobile drawer przenosi sidebar poza root — nasłuch na document.
    document.addEventListener('school-combo:change', (event) => {
      const combo = event.target?.closest?.('.js-school-combo') || event.target;
      if (!comboBelongsToProduct(root, combo)) return;
      if (!combo?.matches?.('.js-school-lesson-location, .js-school-lesson-hours, .js-school-lesson-people')) {
        return;
      }
      // Godziny / osoby z sidebara = wybór pakietu (jak combo package na Pingwinie).
      if (combo.matches('.js-school-lesson-hours, .js-school-lesson-people')) {
        setLessonPackagePicked(root, true);
        syncLessonPeopleHoursUrl();
      }
      if (combo.matches('.js-school-lesson-location')) {
        syncLessonLocationIfChanged();
      }
      syncWidget();
    });

    const onLessonCtaClick = (event) => {
      const link = event.target?.closest?.('.js-school-lesson-cta');
      if (!link) return;
      const inRoot = root.contains(link);
      const inDrawer = Boolean(productSidebarDrawer(root)?.contains(link));
      if (!inRoot && !inDrawer) return;
      if (isLessonPackagePicked(root)) return;
      event.preventDefault();
      showPackageRequiredTip(link);
      scrollToPackagesSection(root, {
        closeDrawer: root._stickySummaryApi?.closeDrawer,
      });
    };
    document.addEventListener('click', onLessonCtaClick, true);
  }

  // SSR nakłada paczkę bieżącego/domyślnego stoku; inline packs — sync od razu.
  // Lekcje (REST): dociągnij+nałóż paczkę z tym samym overlayem „Ładowanie oferty…”
  // co Pingwin/kursy przy zmianie stoku (wcześniej withLoading:false ukrywało wait).
  hydrateLessonDisciplineFromUrl(root);
  // Po nawigacji Narty↔Snowboard przywróć osoby/godziny/stok (sessionStorage) zanim sync paczki.
  consumeLessonUiTransfer(root);
  lastLessonLocation = String(getWidgetState(liveLessonWidget(root) || _initialWidget).location || '').trim();
  if (lastLessonLocation) {
    const inline = root.dataset.schoolCourseContentPacks || '';
    const packs = parseSchoolCourseContentPacks(root);
    const contentKey = lessonContentKeyForRoot(root);
    const cached = slopeHasLessonPack(packs, lastLessonLocation, contentKey);
    if (inline && inline !== '{}') {
      syncLessonLocationContent(root, lastLessonLocation, { withLoading: !cached });
    } else if (root.dataset.schoolCourseContentPacksUrl) {
      syncLessonLocationContent(root, lastLessonLocation, { withLoading: !cached });
      prefetchLessonContentPacks(root);
    }
  }
  syncWidget();
  requestAnimationFrame(() => syncWidget());
  root._syncLessonWidget = syncWidget;
};

const COURSE_BOOKING_HREF = /rezerwacja-kursu/i;

const getCourseDiscipline = (root) => {
  const selected = queryProduct(root, '.school-product-page__sport-toggle input[type="radio"]:checked:not(:disabled)');
  if (!selected) return 'ski';
  return selected.value === 'snowboard' ? 'snowboard' : 'ski';
};

const courseDisciplines = (course) => {
  const raw = Array.isArray(course?.disciplines) ? course.disciplines : [];
  const allowed = [...new Set(raw.map((item) => String(item).toLowerCase()).filter((item) => (
    item === 'narty' || item === 'snowboard'
  )))];
  return allowed.length ? allowed : ['narty', 'snowboard'];
};

const syncCourseDisciplines = (root, course) => {
  const toggle = queryProduct(root, '.school-product-page__sport-toggle');
  if (!toggle) return;
  const allowed = courseDisciplines(course);
  const options = [...toggle.querySelectorAll('.school-product-page__sport-option')];
  options.forEach((label) => {
    const input = label.querySelector('input[type="radio"]');
    const value = (label.dataset.discipline || input?.value) === 'snowboard' ? 'snowboard' : 'narty';
    const show = allowed.includes(value);
    label.hidden = !show;
    if (input) input.disabled = !show;
  });
  const checked = toggle.querySelector('input[type="radio"]:checked');
  const checkedVal = checked?.value === 'snowboard' ? 'snowboard' : 'narty';
  if (!allowed.includes(checkedVal)) {
    const next = toggle.querySelector(`input[type="radio"][value="${allowed[0]}"]`);
    if (next) {
      next.checked = true;
      next.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  toggle.hidden = allowed.length === 0;
};

const lessonSlopeDisciplines = (location = '') => {
  const slope = findLessonSlope(location);
  const raw = Array.isArray(slope?.disciplines) ? slope.disciplines : null;
  if (!raw) {
    // Brak pola = obie dostępne (stare katalogi / default).
    if (slope?.hide_ski || slope?.hide_snowboard) {
      const out = [];
      if (!slope.hide_ski) out.push('narty');
      if (!slope.hide_snowboard) out.push('snowboard');
      return out.length ? out : ['narty'];
    }
    return ['narty', 'snowboard'];
  }
  const allowed = [...new Set(raw.map((item) => String(item).toLowerCase()).filter((item) => (
    item === 'narty' || item === 'snowboard'
  )))];
  return allowed.length ? allowed : ['narty'];
};

const syncLessonDisciplines = (root, location = '') => {
  const toggle = queryProduct(root, '.school-product-page__sport-toggle');
  if (!toggle) return;
  const allowed = lessonSlopeDisciplines(location);
  const options = [...toggle.querySelectorAll('.school-product-page__sport-option')];
  options.forEach((label) => {
    const input = label.querySelector('input[type="radio"]');
    const value = (label.dataset.discipline || input?.value) === 'snowboard' ? 'snowboard' : 'narty';
    const show = allowed.includes(value);
    label.hidden = !show;
    if (input) input.disabled = !show;
  });
  const checked = toggle.querySelector('input[type="radio"]:checked:not(:disabled)');
  const checkedVal = checked?.value === 'snowboard' ? 'snowboard' : 'narty';
  if (!allowed.includes(checkedVal)) {
    const next = toggle.querySelector(`input[type="radio"][value="${allowed[0]}"]`);
    if (next) {
      next.checked = true;
      next.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  toggle.hidden = allowed.length === 0;
};


const setCourseLinkParam = (href, key, value) => {
  try {
    const url = new URL(href, window.location.origin);
    if (!COURSE_BOOKING_HREF.test(url.pathname) && !COURSE_BOOKING_HREF.test(href)) {
      return href;
    }
    url.searchParams.set(key, value);
    // Keep relative paths when the original href was relative.
    if (/^https?:\/\//i.test(href)) {
      return url.toString();
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};

/**
 * Kursy (Pingwin / dorośli): dyscyplina, termin i pakiet
 * muszą trafiać do wszystkich CTA → /rezerwacja-kursu/.
 */
const formatTermDaysLabel = (days) => {
  const count = Number(days) || 0;
  if (count <= 0) return '';
  return `${count} ${count === 1 ? 'dzień' : 'dni'}`;
};

const PL_MONTHS_GEN = [
  'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
  'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia',
];
const PL_MONTHS_CAL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const ICN_CAL = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"></rect><path d="M3 10h18M8 3v4M16 3v4"></path></svg>';
const ICN_X = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"></path></svg>';
const ICN_CHEV = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="m6 9 6 6 6-6"></path></svg>';
const ICN_CHECK = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const isFerieCourse = (course) => {
  const flag = course?.is_ferie;
  return flag === true || flag === 'yes' || flag === 1 || flag === '1';
};

const courseDateFrom = (course) => String(course?.date_from || course?.start || '');
const courseDateTo = (course) => String(course?.date_to || course?.end || '');

/** Lokalna data Y-m-d (jak filtr PHP date_to >= dziś). */
const localTodayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Ukrywa kursy po dacie końca — spójnie z SchoolCoursePricing::filterUpcomingCourses.
 * Gdy wszystkie minęły, zostawia listę (fallback jak w PHP).
 */
const upcomingCoursesOnly = (courses) => {
  if (!Array.isArray(courses) || !courses.length) return [];
  const today = localTodayYmd();
  const upcoming = courses.filter((course) => {
    const to = courseDateTo(course);
    return !to || to >= today;
  });
  return upcoming.length ? upcoming : courses;
};

const courseDisplayName = (course) => {
  if (!course) return '';
  if (isFerieCourse(course)) return String(course.name || 'Kurs feryjny');
  return String(course.name || course.title || course.label || '');
};

const formatTermLong = (from, to, days) => {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end) return '';
  const same = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const core = same
    ? `${start.getDate()} do ${end.getDate()} ${PL_MONTHS_GEN[end.getMonth()]} ${end.getFullYear()}`
    : `${start.getDate()} ${PL_MONTHS_GEN[start.getMonth()]}${start.getFullYear() !== end.getFullYear() ? ` ${start.getFullYear()}` : ''} do ${end.getDate()} ${PL_MONTHS_GEN[end.getMonth()]} ${end.getFullYear()}`;
  const count = Number(days) || 0;
  return count > 0 ? `${core} (${count} dni)` : core;
};

const formatTermShort = (iso) => {
  const date = parseDate(iso);
  if (!date) return '';
  return `${date.getDate()} ${PL_MONTHS_GEN[date.getMonth()].slice(0, 3)}`;
};

const formatTermRangeShort = (from, to, days) => {
  const start = formatTermShort(from);
  const end = formatTermShort(to);
  if (!start || !end) return '';
  const count = Number(days) || 0;
  return count > 0 ? `${start} do ${end} (${count} dni)` : `${start} do ${end}`;
};

const ferieMetaFromCourse = (course) => {
  const groupId = String(course?.ferie_group_id || '');
  const weekRaw = course?.ferie_week;
  if (groupId !== '' && weekRaw !== undefined && weekRaw !== null && weekRaw !== '') {
    return { groupId, week: Number(weekRaw) || 0 };
  }
  const parts = String(course?.id || '').split(':');
  return {
    groupId: parts[1] || groupId || String(course?.voi || course?.id || ''),
    week: parts[2] === 'w1' ? 1 : 0,
  };
};

const partitionCatalogCourses = (courses = []) => {
  const items = [];
  let ferieItem = null;
  const groupsById = new Map();

  courses.forEach((course) => {
    if (!isFerieCourse(course)) {
      items.push({ type: 'course', course });
      return;
    }
    if (!ferieItem) {
      ferieItem = { type: 'ferie', groups: [] };
      items.push(ferieItem);
    }
    const meta = ferieMetaFromCourse(course);
    let group = groupsById.get(meta.groupId);
    if (!group) {
      group = {
        id: meta.groupId,
        voi: String(course.voi || ''),
        days: Number(course.days) || 6,
        weeks: [],
      };
      groupsById.set(meta.groupId, group);
      ferieItem.groups.push(group);
    }
    group.weeks[meta.week] = course;
  });

  return items;
};

const catalogCoursesForRoot = (root) => {
  const catalog = parseSchoolCourseCatalog(root);
  if (!catalog.length) return [];
  const locationCombo = queryProduct(root, '.js-school-course-location');
  const locationKey = locationCombo?.dataset.value
    || root.dataset.location
    || resolveLocationKey(effectiveDestinations(root));
  const slope = findCatalogSlope(catalog, locationKey, effectiveDestinations(root));
  return upcomingCoursesOnly(Array.isArray(slope?.courses) ? slope.courses : []);
};

const comboWantsFerie = (combo) => {
  const flag = combo?.dataset?.isFerie;
  return flag === '1' || flag === 'true' || flag === 'yes';
};

const findCourseByCombo = (courses, combo) => {
  const value = combo?.dataset.value || '';
  const from = combo?.dataset.dateFrom || '';
  const to = combo?.dataset.dateTo || '';
  const wantFerie = comboWantsFerie(combo);

  const byId = courses.find((course) => value && course.id === value);
  if (byId) return byId;

  const byValue = courses.filter((course) => value && (course.value === value || course.id === value));
  if (byValue.length === 1) return byValue[0];
  if (byValue.length > 1) {
    const typed = byValue.find((course) => isFerieCourse(course) === wantFerie);
    if (typed) return typed;
  }

  const byDates = courses.filter((course) => (
    from && to && courseDateFrom(course) === from && courseDateTo(course) === to
  ));
  if (byDates.length === 1) return byDates[0];
  if (byDates.length > 1) {
    const typed = byDates.find((course) => isFerieCourse(course) === wantFerie);
    if (typed) return typed;
  }

  return courses[0] || null;
};

const sameCourse = (a, b) => {
  if (!a || !b) return false;
  if (isFerieCourse(a) !== isFerieCourse(b)) return false;
  if (a.id && b.id && a.id === b.id) return true;
  if (isFerieCourse(a)) {
    const left = ferieMetaFromCourse(a);
    const right = ferieMetaFromCourse(b);
    return left.groupId !== '' && left.groupId === right.groupId && left.week === right.week;
  }
  return courseDateFrom(a) === courseDateFrom(b) && courseDateTo(a) === courseDateTo(b);
};

const closeSchoolCombo = (wrap) => {
  const trigger = wrap.querySelector('.js-school-combo-trigger');
  const list = wrap.querySelector('.js-school-combo-list');
  if (!trigger || !list) return;
  trigger.setAttribute('aria-expanded', 'false');
  list.hidden = true;
};

const openSchoolCombo = (wrap) => {
  const trigger = wrap.querySelector('.js-school-combo-trigger');
  const list = wrap.querySelector('.js-school-combo-list');
  if (!trigger || !list) return;
  trigger.setAttribute('aria-expanded', 'true');
  list.hidden = false;
};

const selectSchoolComboOption = (wrap, option, { silent = false } = {}) => {
  if (!wrap || !option) return;
  const valueEl = wrap.querySelector('.js-school-combo-value');
  const metaEl = wrap.querySelector('.js-school-combo-meta');
  const label = option.dataset.label || option.textContent.trim();
  const days = option.dataset.days || '';

  wrap.dataset.value = option.dataset.value || '';
  wrap.dataset.label = label;
  if (option.dataset.dateFrom) wrap.dataset.dateFrom = option.dataset.dateFrom;
  else delete wrap.dataset.dateFrom;
  if (option.dataset.dateTo) wrap.dataset.dateTo = option.dataset.dateTo;
  else delete wrap.dataset.dateTo;
  if (days) wrap.dataset.days = days;
  else delete wrap.dataset.days;
  // "0" jest falsy — nie wolno gubić ceny 0 zł.
  if (Object.prototype.hasOwnProperty.call(option.dataset, 'price')) {
    wrap.dataset.price = option.dataset.price;
  } else {
    delete wrap.dataset.price;
  }
  if (option.dataset.bookingUrl) wrap.dataset.bookingUrl = option.dataset.bookingUrl;
  else delete wrap.dataset.bookingUrl;
  if (option.dataset.lessonType) wrap.dataset.lessonType = option.dataset.lessonType;
  else delete wrap.dataset.lessonType;
  if (option.dataset.optId) wrap.dataset.optId = option.dataset.optId;
  else delete wrap.dataset.optId;
  if (option.dataset.isFerie) wrap.dataset.isFerie = option.dataset.isFerie;
  else delete wrap.dataset.isFerie;

  if (valueEl) {
    valueEl.textContent = label;
    valueEl.classList.toggle('is-placeholder', !wrap.dataset.value);
  }
  if (metaEl) {
    const text = formatTermDaysLabel(days);
    metaEl.textContent = text;
    metaEl.hidden = !text;
  }

  wrap.querySelectorAll('.js-school-combo-option').forEach((opt) => {
    const on = opt === option;
    opt.classList.toggle('is-selected', on);
    opt.setAttribute('aria-selected', String(on));
  });

  closeSchoolCombo(wrap);
  if (!silent) {
    wrap.dispatchEvent(new CustomEvent('school-combo:change', { bubbles: true }));
  }
};

const parseSchoolCourseCatalog = (root) => {
  const raw = root?.dataset?.schoolCourseCatalog || '';
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** @returns {Record<string, any>} */
const parseSchoolCourseContentPacks = (root) => {
  if (root?._contentPacksState && typeof root._contentPacksState === 'object') {
    return root._contentPacksState;
  }
  const raw = root?.dataset?.schoolCourseContentPacks || '';
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    const state = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    if (root) root._contentPacksState = state;
    return state;
  } catch {
    return {};
  }
};

/** course_key paczek lekcji — PHP: LESSON_CONTENT_KEY_* (+ legacy `lekcje`). */
const LESSON_CONTENT_KEY_LEGACY = 'lekcje';
const LESSON_CONTENT_KEY_CHILDREN = 'lekcje-dzieci';
const LESSON_CONTENT_KEY_ADULTS = 'lekcje-dorosli';
const LESSON_CONTENT_KEY_CHILDREN_SB = 'lekcje-dzieci-snowboard';
const LESSON_CONTENT_KEY_ADULTS_SB = 'lekcje-dorosli-snowboard';

const lessonContentKeyForRoot = (root, discipline = '') => {
  const aud = lessonAudienceForBooking(root) || String(root?.dataset?.lessonAudience || '').toLowerCase();
  const disc = (discipline || resolveLessonDiscipline(root)).toLowerCase();
  const isSb = disc === 'snowboard';
  if (aud === 'children') return isSb ? LESSON_CONTENT_KEY_CHILDREN_SB : LESSON_CONTENT_KEY_CHILDREN;
  return isSb ? LESSON_CONTENT_KEY_ADULTS_SB : LESSON_CONTENT_KEY_ADULTS;
};

/** Czy w cache jest paczka dla stoku (właściwy klucz / ski fallback / legacy). */
const slopeHasLessonPack = (state, slope, contentKey) => {
  if (!state?.by_slope || typeof state.by_slope !== 'object') return false;
  const forSlope = state.by_slope[slope];
  if (!forSlope || typeof forSlope !== 'object') return false;
  const candidates = [contentKey];
  if (contentKey === LESSON_CONTENT_KEY_CHILDREN_SB) candidates.push(LESSON_CONTENT_KEY_CHILDREN);
  if (contentKey === LESSON_CONTENT_KEY_ADULTS_SB) candidates.push(LESSON_CONTENT_KEY_ADULTS);
  candidates.push(LESSON_CONTENT_KEY_LEGACY);
  return candidates.some((key) => key && forSlope[key] && typeof forSlope[key] === 'object');
};

/**
 * Jak PHP resolveLessonPackSections: *-snowboard → audience → legacy `lekcje`.
 */
const resolveLessonContentPack = (packs, root, slopeSlug = '') => {
  const discipline = resolveLessonDiscipline(root);
  const keys = [];
  if (discipline === 'snowboard') {
    keys.push(lessonContentKeyForRoot(root, 'snowboard'));
  }
  keys.push(lessonContentKeyForRoot(root, 'ski'));
  keys.push(LESSON_CONTENT_KEY_LEGACY);
  const seen = new Set();
  for (const key of keys) {
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const pack = resolveContentPack(packs, key, slopeSlug);
    if (pack) return { pack, key };
  }
  return { pack: null, key: keys[0] || LESSON_CONTENT_KEY_ADULTS };
};

const mergeContentPacksState = (root, fragment) => {
  const state = parseSchoolCourseContentPacks(root);
  if (!root) return state;
  if (!fragment || typeof fragment !== 'object') {
    root._contentPacksState = state;
    return state;
  }
  if (fragment.by_slope && typeof fragment.by_slope === 'object') {
    state.by_slope = state.by_slope && typeof state.by_slope === 'object' ? state.by_slope : {};
    Object.keys(fragment.by_slope).forEach((slope) => {
      const incoming = fragment.by_slope[slope];
      if (!incoming || typeof incoming !== 'object') return;
      state.by_slope[slope] = {
        ...(state.by_slope[slope] && typeof state.by_slope[slope] === 'object' ? state.by_slope[slope] : {}),
        ...incoming,
      };
    });
  }
  if (fragment.by_course && typeof fragment.by_course === 'object') {
    state.by_course = {
      ...(state.by_course && typeof state.by_course === 'object' ? state.by_course : {}),
      ...fragment.by_course,
    };
  }
  root._contentPacksState = state;
  return state;
};

const fetchContentPacksForSlope = (root, slope = '') => {
  const endpoint = root?.dataset?.schoolCourseContentPacksUrl || '';
  if (!endpoint) return Promise.resolve(parseSchoolCourseContentPacks(root));
  const audience = root.dataset.schoolCourseContentPacksAudience || 'lessons';
  let normalized = String(slope || '').trim();
  if (normalized && DESTINATION_TO_LOCATION[normalized]) {
    normalized = DESTINATION_TO_LOCATION[normalized];
  }
  const state = parseSchoolCourseContentPacks(root);
  if (normalized) {
    const contentKey = lessonContentKeyForRoot(root);
    if (slopeHasLessonPack(state, normalized, contentKey)) {
      return Promise.resolve(state);
    }
  } else if (state.by_slope && Object.keys(state.by_slope).length > 0) {
    return Promise.resolve(state);
  }
  if (!root._contentPackFetches) root._contentPackFetches = {};
  const key = `${audience}|${normalized || '*'}`;
  if (root._contentPackFetches[key]) return root._contentPackFetches[key];

  let url;
  try {
    url = new URL(endpoint, window.location.origin);
  } catch {
    return Promise.resolve(state);
  }
  url.searchParams.set('audience', audience);
  if (normalized) url.searchParams.set('slope', normalized);

  const pending = fetch(url.toString(), { credentials: 'same-origin' })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`packs ${res.status}`))))
    .then((data) => mergeContentPacksState(root, data))
    .catch(() => {
      delete root._contentPackFetches[key];
      return parseSchoolCourseContentPacks(root);
    });
  root._contentPackFetches[key] = pending;
  return pending;
};

const prefetchLessonContentPacks = (root) => {
  if (!root?.dataset?.schoolCourseContentPacksUrl) return;
  const run = () => {
    fetchContentPacksForSlope(root, '').catch(() => {});
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2500 });
  } else {
    window.setTimeout(run, 1200);
  }
};

const contentKeyFromCourse = (course) => {
  if (!course || typeof course !== 'object') return '';
  const explicit = String(course.content_key || '').trim();
  if (explicit) return explicit;
  const id = String(course.id || '').trim();
  if (!id) return '';
  return id.split(':')[0] || '';
};

/** Kolejne synci lekcji (init + zmiana stoku) — ignoruj spóźniony apply ze starego fetcha. */
let lessonContentSyncGen = 0;

/**
 * Podmiana sekcji marketingowych lekcji po zmianie stoku.
 * Hero (sidebar) leci z paczki — jak Pingwin, zachowaj wybory i zbindować od nowa.
 * Paczki lekcji: lazy-load z REST (bez ~450 KB w HTML) + ten sam overlay „Ładowanie oferty…”.
 *
 * @param {HTMLElement} root
 * @param {string} [locationId]
 * @param {{ withLoading?: boolean }} [opts]
 */
const syncLessonLocationContent = (root, locationId = '', opts = {}) => {
  const loc = String(locationId || '').trim();
  if (!loc || !root) return;

  const gen = ++lessonContentSyncGen;
  const withLoading = opts.withLoading !== false;
  const hasInline = Boolean(
    root.dataset.schoolCourseContentPacks
    && root.dataset.schoolCourseContentPacks !== '{}'
  );
  const hasUrl = Boolean(root.dataset.schoolCourseContentPacksUrl);
  if (!hasInline && !hasUrl && !root._contentPacksState) return;

  const apply = () => {
    if (gen !== lessonContentSyncGen) return;

    const prevWidget = liveLessonWidget(root);
    const prevState = prevWidget ? getWidgetState(prevWidget) : null;
    if (prevState) prevState.location = loc;

    const contentKey = lessonContentKeyForRoot(root);
    syncTermContentSections(
      root,
      { id: contentKey, content_key: contentKey },
      loc,
    );

    if (gen !== lessonContentSyncGen) return;

    const widget = restoreLessonWidgetAfterContentSwap(root, prevState);
    // Hero z paczki ma zwykle „Dorośli” / „Narty” — przywróć type[] i wybrany sport.
    hydrateLessonAudience(root);
    hydrateLessonDisciplineFromUrl(root);
    const sport = resolveLessonDiscipline(root);
    syncSportBadge(root, sport === 'snowboard' ? 'snowboard' : 'ski');
    root.querySelectorAll('.js-school-price-card').forEach((card) => {
      delete card.dataset.packageBound;
    });
    if (widget) initLessonPackageCards(root, widget);
    if (typeof root._syncLessonWidget === 'function') {
      root._syncLessonWidget();
    }
    root.dataset.lessonContentSlope = loc;
    document.dispatchEvent(new CustomEvent('hski:term-content-updated'));
    ensureProductGalleryLightbox();
  };

  const run = () => {
    if (hasUrl) return fetchContentPacksForSlope(root, loc).then(apply);
    apply();
    return undefined;
  };

  if (withLoading) {
    runWithCourseContentLoading(root, run);
    return;
  }
  Promise.resolve(run()).catch(() => {});
};

/** Przywróć stok / osoby / godziny / sport po swapie hero (paczka treści). */
const restoreLessonWidgetAfterContentSwap = (root, state = null) => {
  const widget = liveLessonWidget(root);
  if (!widget) return null;
  if (!state || typeof state !== 'object') return widget;

  const locationCombo = widget.querySelector(
    '.js-school-lesson-location.js-school-combo, .js-school-combo.js-school-lesson-location',
  );
  if (locationCombo && state.location) {
    const locOpt = [...locationCombo.querySelectorAll('.js-school-combo-option')].find((opt) => (
      opt.dataset.value === String(state.location)
    ));
    if (locOpt) selectSchoolComboOption(locationCombo, locOpt, { silent: true });
  }

  if (state.people) {
    selectLessonPeople(widget, state.people, { force: true });
  }
  if (state.hours) {
    selectLessonHours(widget, state.hours, state.lessonType || 'single', { force: true });
  }

  const sportValue = state.discipline === 'snowboard' ? 'snowboard' : 'ski';
  const sportInput = widget.querySelector(`[name="sport"][value="${sportValue}"]`);
  if (sportInput instanceof HTMLInputElement) {
    sportInput.checked = true;
  }

  applyLessonCatalogFilters(root, widget);
  return widget;
};

/**
 * Paczka: by_slope[stok][course] → by_course[course] → legacy flat[course].
 */
const resolveContentPack = (packs, courseKey, slopeSlug = '') => {
  if (!packs || typeof packs !== 'object' || !courseKey) return null;
  let slope = String(slopeSlug || '').trim();
  if (slope && DESTINATION_TO_LOCATION[slope]) {
    slope = DESTINATION_TO_LOCATION[slope];
  }
  if (slope && packs.by_slope && typeof packs.by_slope === 'object') {
    const forSlope = packs.by_slope[slope];
    if (forSlope && typeof forSlope === 'object' && forSlope[courseKey] && typeof forSlope[courseKey] === 'object') {
      return forSlope[courseKey];
    }
  }
  if (packs.by_course && typeof packs.by_course === 'object'
    && packs.by_course[courseKey] && typeof packs.by_course[courseKey] === 'object') {
    return packs.by_course[courseKey];
  }
  const legacy = packs[courseKey];
  if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)
    && !legacy.by_slope && !legacy.by_course) {
    return legacy;
  }
  return null;
};

/**
 * Kolejność sekcji marketingowych (jak PHP SWAP_SECTION_KEYS).
 * Używana przy wstawianiu brakujących sekcji z paczki.
 */
const TERM_CONTENT_SECTION_ORDER = [
  'hero',
  'film',
  'program',
  'offer',
  'features',
  'badges',
  'attractions',
  'schedule',
  'gallery',
  'location',
  'faq',
];

/** Hero siedzi w sidebarze — nie wstrzykuj do main-col. */
const TERM_CONTENT_NO_INJECT = new Set(['hero']);

/**
 * Blok `hski/school-product-badges` renderuje się w `.container` — w main-col
 * zostawiamy sam inner HTML sekcji.
 */
const unwrapPackSectionHtml = (html) => {
  const raw = String(html || '').trim();
  if (!raw) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = raw;
  if (tmp.children.length === 1) {
    const only = tmp.children[0];
    if (
      only instanceof HTMLElement
      && only.classList.contains('container')
      && only.classList.contains('school-product-page__content')
    ) {
      return only.innerHTML.trim() || raw;
    }
  }
  return raw;
};

/**
 * Wstaw brakującą sekcję z paczki w main-col wg kolejności TERM_CONTENT_SECTION_ORDER.
 * @returns {HTMLElement|null}
 */
const injectTermContentSection = (root, section, html) => {
  if (!root || !html || TERM_CONTENT_NO_INJECT.has(section)) return null;
  const mainCol = root.querySelector('.school-product-page__main-col')
    || root.querySelector('.js-term-content[data-term-section="offer"]')?.parentElement
    || root.querySelector('.js-term-content[data-term-section="features"]')?.parentElement
    || root;
  if (!mainCol) return null;

  const wrap = document.createElement('div');
  wrap.className = 'js-term-content';
  wrap.dataset.termSection = section;
  wrap.dataset.termContentInjected = '1';
  wrap.innerHTML = unwrapPackSectionHtml(html);

  const orderIdx = TERM_CONTENT_SECTION_ORDER.indexOf(section);
  // Cennik nie jest w TERM_CONTENT_* — sekcje przed gallery wstawiaj przed pakietami.
  const galleryIdx = TERM_CONTENT_SECTION_ORDER.indexOf('gallery');
  // Nieznane klucze z paczki (np. careers-page): przed galerią / FAQ, nie na górze kolumny.
  if (orderIdx < 0) {
    const afterPackages = mainCol.querySelector(':scope > .js-school-packages, :scope > #section-cennik')
      || mainCol.querySelector('.js-school-packages, #section-cennik');
    const beforeGallery = mainCol.querySelector(':scope > .js-term-content[data-term-section="gallery"]')
      || root.querySelector('.js-term-content[data-term-section="gallery"]')
      || mainCol.querySelector('#section-galeria');
    const beforeFaq = mainCol.querySelector(':scope > .js-term-content[data-term-section="faq"]')
      || root.querySelector('.js-term-content[data-term-section="faq"]')
      || mainCol.querySelector('#section-faq');
    if (beforeGallery && mainCol.contains(beforeGallery)) {
      mainCol.insertBefore(wrap, beforeGallery);
      return wrap;
    }
    if (beforeFaq && mainCol.contains(beforeFaq)) {
      mainCol.insertBefore(wrap, beforeFaq);
      return wrap;
    }
    if (afterPackages && mainCol.contains(afterPackages)) {
      afterPackages.after(wrap);
      return wrap;
    }
    mainCol.appendChild(wrap);
    return wrap;
  }
  if (orderIdx >= 0 && (galleryIdx < 0 || orderIdx < galleryIdx)) {
    const packages = mainCol.querySelector(':scope > .js-school-packages, :scope > #section-cennik')
      || mainCol.querySelector('.js-school-packages, #section-cennik');
    if (packages && mainCol.contains(packages)) {
      packages.before(wrap);
      return wrap;
    }
  }
  // Wstaw przed następną istniejącą sekcją z listy (w main-col).
  for (let i = orderIdx + 1; i < TERM_CONTENT_SECTION_ORDER.length; i += 1) {
    const next = mainCol.querySelector(`:scope > .js-term-content[data-term-section="${TERM_CONTENT_SECTION_ORDER[i]}"]`)
      || root.querySelector(`.js-term-content[data-term-section="${TERM_CONTENT_SECTION_ORDER[i]}"]`);
    if (next && mainCol.contains(next)) {
      mainCol.insertBefore(wrap, next);
      return wrap;
    }
  }
  // Albo zaraz po poprzedniej.
  for (let i = orderIdx - 1; i >= 0; i -= 1) {
    const prev = mainCol.querySelector(`:scope > .js-term-content[data-term-section="${TERM_CONTENT_SECTION_ORDER[i]}"]`)
      || root.querySelector(`.js-term-content[data-term-section="${TERM_CONTENT_SECTION_ORDER[i]}"]`);
    if (prev && mainCol.contains(prev)) {
      prev.after(wrap);
      return wrap;
    }
  }
  mainCol.appendChild(wrap);
  return wrap;
};

/**
 * Podmienia sekcje marketingowe z paczki (stok + course_key).
 * Brak HTML w paczce → przywraca SSR default (nie klei poprzedniego stoku).
 * Sekcja jest w paczce, a nie na hubie → wstawia wrapper (np. odznaki).
 * Sekcja była wstrzyknięta, a w nowej paczce jej brak → usuwa.
 */
/** Klucze paczki jak PHP contentKeysForDiscipline: SB → baza. */
const contentKeysForDiscipline = (courseKey, discipline = 'ski') => {
  const raw = String(courseKey || '').trim();
  if (!raw) return [];
  const base = raw.replace(/-snowboard$/i, '') || raw;
  const keys = [];
  if (String(discipline || '').toLowerCase() === 'snowboard') {
    keys.push(`${base}-snowboard`);
  }
  keys.push(base);
  if (raw !== base) keys.push(raw);
  return [...new Set(keys.filter(Boolean))];
};

const resolveCourseContentPack = (packs, course, slopeSlug = '', discipline = 'ski') => {
  const key = contentKeyFromCourse(course);
  for (const candidate of contentKeysForDiscipline(key, discipline)) {
    const pack = resolveContentPack(packs, candidate, slopeSlug);
    if (pack) return { pack, key: candidate };
  }
  return { pack: null, key };
};

const syncTermContentSections = (root, course, slopeSlug = '') => {
  if (!root) return;
  const packs = parseSchoolCourseContentPacks(root);
  const isLessons = Boolean(
    root.dataset.schoolCourseContentPacksAudience === 'lessons'
    || root.querySelector('.js-school-lesson-widget')
  );
  let pack = null;
  if (isLessons) {
    pack = resolveLessonContentPack(packs, root, slopeSlug).pack;
  } else {
    const discipline = getCourseDiscipline(root) || lessonDisciplineFromUrl() || 'ski';
    pack = resolveCourseContentPack(packs, course, slopeSlug, discipline).pack;
  }
  // Bez `packages`: karty cennika zawsze z katalogu Carbon.
  const packKeys = pack && typeof pack === 'object'
    ? Object.keys(pack).filter((k) => k && k !== 'packages' && typeof pack[k] === 'string')
    : [];
  const sectionOrder = [
    ...TERM_CONTENT_SECTION_ORDER,
    ...packKeys.filter((k) => !TERM_CONTENT_SECTION_ORDER.includes(k)),
  ];

  sectionOrder.forEach((section) => {
    const htmlRaw = pack && typeof pack[section] === 'string' ? pack[section].trim() : '';
    const html = htmlRaw ? unwrapPackSectionHtml(htmlRaw) : '';
    let nodes = [...root.querySelectorAll(`.js-term-content[data-term-section="${section}"]`)];

    if (!nodes.length) {
      if (!html || TERM_CONTENT_NO_INJECT.has(section)) return;
      const injected = injectTermContentSection(root, section, html);
      if (injected) nodes = [injected];
      else return;
    }

    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (!html) {
        if (node.dataset.termContentInjected === '1') {
          node.remove();
          return;
        }
        if (!node.dataset.termContentDefault) {
          node.dataset.termContentDefault = node.innerHTML;
        }
        const fallback = node.dataset.termContentDefault || '';
        if (fallback && node.innerHTML !== fallback) {
          node.innerHTML = fallback;
        }
        return;
      }
      if (!node.dataset.termContentDefault && node.dataset.termContentInjected !== '1') {
        node.dataset.termContentDefault = node.innerHTML;
      }
      if (node.innerHTML !== html) {
        node.innerHTML = html;
      }
    });
  });

  // Usuń wstrzyknięte sekcje ekstra, których nie ma w nowej paczce (np. careers-page tylko na Harendzie).
  root.querySelectorAll('.js-term-content[data-term-content-injected="1"]').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const section = node.dataset.termSection || '';
    if (!section || TERM_CONTENT_NO_INJECT.has(section)) return;
    if (sectionOrder.includes(section) && pack && typeof pack[section] === 'string' && pack[section].trim()) {
      return;
    }
    node.remove();
  });
};

/** PhotoSwipe po podmianie galerii w paczce (listenery giną ze starym węzłem). */
const ensureProductGalleryLightbox = () => {
  if (!document.querySelector('.school-product-page__gallery-grid.gallery-js, .js-gallery-open-all, [class*="gallery-js"]')) {
    return;
  }
  import('../../../js/Lightbox.js')
    .then((m) => (typeof m.refreshLightbox === 'function' ? m.refreshLightbox() : m.default()))
    .catch(() => {});
};

/** Minimalny czas overlayu — sync jest synchroniczny, bez tego flash byłby niewidoczny. */
const COURSE_CONTENT_LOADING_MS = 160;
let courseContentLoadingToken = 0;

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
);

const ensureCourseContentLoadingEl = (root) => {
  let el = root.querySelector('.js-school-course-content-loading');
  if (el) return el;
  el = document.createElement('div');
  el.className = 'school-product-page__content-loading js-school-course-content-loading';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = `
    <div class="school-product-page__content-loading-panel" role="status" aria-live="polite">
      <span class="school-product-page__content-loading-spinner" aria-hidden="true"></span>
      <span class="school-product-page__content-loading-label">Ładowanie oferty…</span>
    </div>
  `;
  root.appendChild(el);
  return el;
};

/**
 * Overlay przed pełną nawigacją (Narty↔SB / zmiana produktu).
 * Preferuje boot SSR (inline CSS) — widoczny zanim school-product.css dociągnie content-loading.
 * Double rAF w assignAfterPaint — żeby przeglądarka namalowała overlay przed unload.
 */
const showLeavingPageLoader = (root) => {
  if (!root) return;
  const boot = root.querySelector('.js-school-product-boot-loading');
  if (boot) {
    root.classList.add('is-school-booting');
    root.setAttribute('aria-busy', 'true');
    boot.setAttribute('aria-hidden', 'false');
    return;
  }
  const el = ensureCourseContentLoadingEl(root);
  root.classList.add('is-course-content-loading');
  root.setAttribute('aria-busy', 'true');
  el.setAttribute('aria-hidden', 'false');
};

const assignAfterPaint = (url) => {
  const go = () => {
    window.location.assign(url);
  };
  // Daj przeglądarce namalować overlay (transition 0.18s) zanim unload.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.setTimeout(go, prefersReducedMotion() ? 0 : 50);
    });
  });
};

const hideCourseContentLoading = (root, token) => {
  if (!root || token !== courseContentLoadingToken) return;
  root.classList.remove('is-course-content-loading');
  root.removeAttribute('aria-busy');
  const el = root.querySelector('.js-school-course-content-loading');
  if (el) el.setAttribute('aria-hidden', 'true');
  root.querySelector('.js-school-course-term-modal')?.classList.remove('is-content-updating');
};

/**
 * Overlay na treści przy zmianie kursu/lokalizacji (nie przy samym pakiecie).
 * Przy otwartym modalu terminu — wskaźnik w stopce modala (dialog top-layer).
 * @param {HTMLElement} root
 * @param {() => void} fn
 * @param {{ onDone?: () => void }} [opts]
 */
const runWithCourseContentLoading = (root, fn, opts = {}) => {
  const onDone = typeof opts?.onDone === 'function' ? opts.onDone : null;
  if (!root || typeof fn !== 'function') {
    fn?.();
    onDone?.();
    return;
  }
  const token = ++courseContentLoadingToken;
  const modal = root.querySelector('.js-school-course-term-modal');
  const modalOpen = Boolean(modal?.open);
  const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const minMs = prefersReducedMotion() ? 80 : COURSE_CONTENT_LOADING_MS;

  if (modalOpen && modal) {
    modal.classList.add('is-content-updating');
  } else {
    const el = ensureCourseContentLoadingEl(root);
    root.classList.add('is-course-content-loading');
    root.setAttribute('aria-busy', 'true');
    el.setAttribute('aria-hidden', 'false');
  }

  const finish = () => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const wait = Math.max(0, minMs - (now - started));
    window.setTimeout(() => {
      hideCourseContentLoading(root, token);
      if (token === courseContentLoadingToken) onDone?.();
    }, wait);
  };

  // Daj przeglądarce namalować overlay przed ciężką podmianą DOM / fetch paczek.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (token !== courseContentLoadingToken) return;
      let result;
      try {
        result = fn();
      } catch (err) {
        finish();
        return;
      }
      if (result && typeof result.then === 'function') {
        Promise.resolve(result).then(finish, finish);
        return;
      }
      finish();
    });
  });
};

/** Mobile sticky drawer trzyma sidebar poza `.js-school-product` — combo trzeba szukać i tu. */
const productSidebarDrawer = (root) => {
  const id = root?.dataset?.offerSidebarMobileId || '';
  if (!id) return null;
  return document.querySelector(`.offer-page__sidebar-drawer[data-summary-root-id="${CSS.escape(id)}"]`);
};

const queryProduct = (root, selector) => (
  root?.querySelector(selector) || productSidebarDrawer(root)?.querySelector(selector) || null
);

/** Combo terminu — po swapie paczki `hero` węzeł jest nowy, nie trzymać starej referencji. */
const liveTermCombo = (root) => (
  root?.querySelector?.('.school-product-page__fields .js-school-combo[data-combo="term"]')
  || queryProduct(root, '.js-school-combo[data-combo="term"]')
);

/** Sidebar lekcji — też w paczce `hero`, po swapie stoku trzeba brać żywy węzeł. */
const liveLessonWidget = (root) => (
  root?.querySelector?.('.js-school-lesson-widget')
  || productSidebarDrawer(root)?.querySelector?.('.js-school-lesson-widget')
  || null
);

const comboBelongsToProduct = (root, combo) => {
  if (!(combo instanceof HTMLElement) || !root) return false;
  if (root.contains(combo)) return true;
  const drawer = combo.closest('.offer-page__sidebar-drawer');
  if (!drawer) return false;
  const id = root.dataset.offerSidebarMobileId || '';
  return id !== '' && drawer.dataset.summaryRootId === id;
};

const findCatalogSlope = (catalog, locationKey, destinations = []) => {
  if (!catalog.length) return null;
  const loc = String(locationKey || '');
  const dests = destinations.map(String);
  return catalog.find((slope) => (
    slope.id === loc
    || slope.destination === loc
    || dests.includes(slope.id)
    || dests.includes(slope.destination)
  )) || catalog[0];
};

const updateCourseTermTrigger = (combo, course) => {
  if (!combo) return;
  const valueEl = combo.querySelector('.js-school-combo-value');
  const metaEl = combo.querySelector('.js-school-combo-meta');
  const name = courseDisplayName(course) || combo.dataset.label || '';
  const from = courseDateFrom(course) || combo.dataset.dateFrom || '';
  const to = courseDateTo(course) || combo.dataset.dateTo || '';
  const days = Number(course?.days) || Number(combo.dataset.days) || 0;
  if (valueEl) valueEl.textContent = name;
  if (metaEl) {
    const text = formatTermRangeShort(from, to, days) || formatTermDaysLabel(days);
    metaEl.textContent = text;
    metaEl.hidden = !text;
  }
};

const enhanceCourseTermTrigger = (combo) => {
  const trigger = combo.querySelector('.js-school-combo-trigger');
  if (!trigger || trigger.dataset.termPickerReady === '1') return;
  trigger.dataset.termPickerReady = '1';
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-controls', 'school-course-term-modal');

  const valueEl = combo.querySelector('.js-school-combo-value');
  const metaEl = combo.querySelector('.js-school-combo-meta');
  const chevron = combo.querySelector('.school-product-page__combo-chevron');

  const cal = document.createElement('span');
  cal.className = 'school-product-page__combo-cal';
  cal.setAttribute('aria-hidden', 'true');
  cal.innerHTML = ICN_CAL;

  const copy = document.createElement('span');
  copy.className = 'school-product-page__combo-copy';
  if (valueEl) copy.appendChild(valueEl);
  if (metaEl) copy.appendChild(metaEl);
  trigger.replaceChildren();
  trigger.append(cal, copy);
  if (chevron) trigger.appendChild(chevron);
};

/** Oznacz combo jako term-picker (klasy / trigger) — wołane po każdym swapie hero. */
const prepareCourseTermCombo = (root) => {
  const combo = liveTermCombo(root);
  if (!combo) return null;
  combo.dataset.termPicker = '1';
  combo.classList.add('js-school-course-term', 'school-product-page__combo--course-term', 'is-term-picker');
  enhanceCourseTermTrigger(combo);
  return combo;
};

const courseTermModalMarkup = () => `
  <div class="school-course-term-modal__panel">
    <div class="school-course-term-modal__head">
      <div>
        <p class="school-course-term-modal__eyebrow">Wybór terminu</p>
        <h3 id="school-course-term-modal-title">Wybierz termin kursu</h3>
      </div>
      <button type="button" class="school-course-term-modal__close js-term-modal-close" aria-label="Zamknij">${ICN_X}</button>
    </div>
    <div class="school-course-term-modal__cols">
      <div class="school-course-term-modal__list js-term-modal-list"></div>
      <div class="school-course-term-modal__aside">
        <div class="js-term-modal-summary"></div>
        <div class="js-term-modal-calendar"></div>
        <div class="js-term-modal-legend"></div>
      </div>
    </div>
    <div class="school-course-term-modal__foot">
      <div class="school-course-term-modal__dates js-term-modal-dates"></div>
      <div class="school-course-term-modal__loading" role="status" aria-live="polite">
        <span class="school-course-term-modal__loading-spinner" aria-hidden="true"></span>
        <span>Ładowanie oferty…</span>
      </div>
      <button type="button" class="btn btn--primary btn--no-arrow school-course-term-modal__confirm js-term-modal-confirm" aria-label="Wybierz">${ICN_CHECK}</button>
    </div>
  </div>
`;

const ensureCourseTermModal = (root) => {
  let dialog = root.querySelector('.js-school-course-term-modal');
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'school-course-term-modal js-school-course-term-modal';
  dialog.id = 'school-course-term-modal';
  dialog.setAttribute('aria-labelledby', 'school-course-term-modal-title');
  dialog.innerHTML = courseTermModalMarkup();
  root.appendChild(dialog);
  return dialog;
};

const calendarSlotsFromCourses = (courses) => {
  const slots = [];
  courses.forEach((course) => {
    const from = courseDateFrom(course);
    const to = courseDateTo(course);
    if (!from || !to) return;
    const ferie = isFerieCourse(course);
    const meta = ferie ? ferieMetaFromCourse(course) : null;
    slots.push({
      id: ferie ? `f:${meta.groupId}:${meta.week}` : `t:${course.id || course.value}`,
      kind: ferie ? 'ferie' : 'course',
      groupId: meta?.groupId || '',
      s: from,
      e: to,
      course,
    });
  });
  return slots;
};

const ensureChangeTermButton = (root) => {
  const bind = (btn) => {
    if (!btn || btn.dataset.bound === '1') return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      const combo = prepareCourseTermCombo(root);
      if (!combo) return;
      combo.dispatchEvent(new CustomEvent('school-term-picker:open', { bubbles: true }));
    });
  };

  root.querySelectorAll('.js-school-change-term').forEach(bind);
  if (root.querySelector('.js-school-change-term')) return;

  const sub = root.querySelector('.school-product-page__page-subtitle');
  if (!sub) return;

  let row = sub.closest('.school-product-page__subtitle-row');
  if (!row) {
    row = document.createElement('div');
    row.className = 'school-product-page__subtitle-row';
    [...sub.classList].forEach((cls) => {
      if (/^(mb-|mt-|pb-|pt-)/.test(cls)) {
        row.classList.add(cls);
        sub.classList.remove(cls);
      }
    });
    sub.parentNode.insertBefore(row, sub);
    row.appendChild(sub);
  }

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'school-product-page__change-term js-school-change-term';
  btn.innerHTML = `<span class="school-product-page__change-term-icon" aria-hidden="true">${ICN_CAL}</span> Zmień termin`;
  row.appendChild(btn);
  bind(btn);
};

const initCourseTermPicker = (root) => {
  if (!parseSchoolCourseCatalog(root).length) return;
  // Po każdym swapie hero: nowy DOM combo — klasy + trigger kalendarza.
  if (!prepareCourseTermCombo(root)) return;

  // Modal + listenery raz na root (dialog żyje poza hero).
  if (root.dataset.termPickerBound === '1') {
    root._refreshTermPicker?.();
    ensureChangeTermButton(root);
    return;
  }
  root.dataset.termPickerBound = '1';

  const dialog = ensureCourseTermModal(root);
  const listEl = dialog.querySelector('.js-term-modal-list');
  const summaryEl = dialog.querySelector('.js-term-modal-summary');
  const calendarEl = dialog.querySelector('.js-term-modal-calendar');
  const legendEl = dialog.querySelector('.js-term-modal-legend');
  const datesEl = dialog.querySelector('.js-term-modal-dates');

  const state = {
    calY: null,
    calM: null,
    ferieOpen: false,
    /** Snapshot combo przy otwarciu — przywracany przy anulowaniu (X / Escape). */
    openedSnapshot: null,
    /** Potwierdzenie w toku — nie przywracaj snapshota przy close. */
    committing: false,
  };

  const focusCal = (iso) => {
    const date = parseDate(iso);
    if (!date) return;
    state.calY = date.getFullYear();
    state.calM = date.getMonth();
  };

  const captureTermSnapshot = () => {
    const combo = liveTermCombo(root);
    if (!combo) return null;
    return {
      value: combo.dataset.value || '',
      label: combo.dataset.label || '',
      dateFrom: combo.dataset.dateFrom || '',
      dateTo: combo.dataset.dateTo || '',
      days: combo.dataset.days || '',
      isFerie: combo.dataset.isFerie || '',
      price: Object.prototype.hasOwnProperty.call(combo.dataset, 'price') ? combo.dataset.price : null,
    };
  };

  const restoreTermSnapshot = (snap) => {
    if (!snap) return;
    const combo = liveTermCombo(root);
    if (!combo) return;
    const match = [...combo.querySelectorAll('.js-school-combo-option')].find((opt) => (
      opt.dataset.value === snap.value
    ));
    if (match) {
      selectSchoolComboOption(combo, match, { silent: true });
    } else {
      combo.dataset.value = snap.value;
      if (snap.dateFrom) combo.dataset.dateFrom = snap.dateFrom;
      else delete combo.dataset.dateFrom;
      if (snap.dateTo) combo.dataset.dateTo = snap.dateTo;
      else delete combo.dataset.dateTo;
      if (snap.days) combo.dataset.days = snap.days;
      else delete combo.dataset.days;
      if (snap.isFerie) combo.dataset.isFerie = snap.isFerie;
      else delete combo.dataset.isFerie;
      if (snap.price !== null) combo.dataset.price = snap.price;
      else delete combo.dataset.price;
    }
    if (snap.label) combo.dataset.label = snap.label;
    const course = findCourseByCombo(catalogCoursesForRoot(root), combo);
    updateCourseTermTrigger(combo, course);
  };

  /** Tylko UI combo/modala — bez swapu treści strony (to robi confirm). */
  const applyCourse = (course) => {
    const combo = liveTermCombo(root);
    if (!course || !combo) return;
    const courseId = String(course.id || '');
    const ferie = isFerieCourse(course);
    const displayName = courseDisplayName(course);
    const match = [...combo.querySelectorAll('.js-school-combo-option')].find((opt) => (
      courseId !== '' && opt.dataset.value === courseId
    )) || [...combo.querySelectorAll('.js-school-combo-option')].find((opt) => {
      const sameDates = opt.dataset.dateFrom === courseDateFrom(course)
        && opt.dataset.dateTo === courseDateTo(course);
      if (!sameDates) return false;
      const optFerie = opt.dataset.isFerie === '1' || opt.dataset.isFerie === 'true';
      return optFerie === ferie;
    });
    if (match) selectSchoolComboOption(combo, match, { silent: true });
    else {
      combo.dataset.value = courseId || course.value || '';
      combo.dataset.dateFrom = courseDateFrom(course);
      combo.dataset.dateTo = courseDateTo(course);
      combo.dataset.days = String(course.days || '');
      if (ferie) combo.dataset.isFerie = '1';
      else delete combo.dataset.isFerie;
    }
    // Zawsze krótka nazwa kursu (np. „Kurs feryjny”), nie title z regionami.
    if (displayName) combo.dataset.label = displayName;
    updateCourseTermTrigger(combo, course);
  };

  const renderModal = () => {
    const combo = liveTermCombo(root);
    if (!combo) return;
    const courses = catalogCoursesForRoot(root);
    const selected = findCourseByCombo(courses, combo);
    const selectedFerie = isFerieCourse(selected);
    const selectedMeta = selectedFerie ? ferieMetaFromCourse(selected) : null;
    const items = partitionCatalogCourses(courses);
    const from = courseDateFrom(selected);
    const to = courseDateTo(selected);
    const days = Number(selected?.days) || 0;

    if (state.calY == null) focusCal(from);

    const ferieBlock = (groups) => {
      if (!groups.length) return '';
      const open = state.ferieOpen || selectedFerie;
      const sub = selectedFerie
        ? (selected.voi ? `Ferie zimowe, ${selected.voi}` : 'Ferie zimowe')
        : 'Ferie zimowe, wybierz termin dla swojego regionu';
      let html = `<button type="button" class="school-course-term-modal__ferie-head${(selectedFerie || open) ? ' is-on' : ''}" data-term-action="toggle-ferie">
        <span class="school-course-term-modal__ferie-cal">${ICN_CAL}</span>
        <span class="school-course-term-modal__ferie-copy"><b>Kurs feryjny</b><small>${escapeHtml(sub)}</small></span>
        <span class="school-course-term-modal__ferie-chev${open ? ' is-open' : ''}">${ICN_CHEV}</span>
      </button>`;
      if (!open) return html;
      html += '<div class="school-course-term-modal__ferie-groups">';
      groups.forEach((group) => {
        const gsel = selectedFerie && selectedMeta?.groupId === group.id;
        const voi = group.voi || 'Wybierz region';
        html += `<div class="school-course-term-modal__ferie-group${gsel ? ' is-on' : ''}">
          <button type="button" class="school-course-term-modal__ferie-row" data-term-action="ferie-group" data-group-id="${group.id}">
            <span class="school-course-term-modal__ferie-check${gsel ? ' is-on' : ''}">${gsel ? ICN_CHECK : ''}</span>
            <span><b>${escapeHtml(voi)}</b><small>kurs trwa ${group.days} dni</small></span>
          </button>`;
        if (gsel) {
          html += '<div class="school-course-term-modal__ferie-weeks">';
          group.weeks.forEach((weekCourse, index) => {
            if (!weekCourse) return;
            const wsel = gsel && selectedMeta?.week === index;
            html += `<button type="button" class="school-course-term-modal__ferie-week${wsel ? ' is-on' : ''}" data-term-action="ferie-week" data-group-id="${group.id}" data-week="${index}">
              <span class="school-course-term-modal__ferie-radio"></span>
              <b>Tydzień ${index + 1}</b>
              <span class="school-course-term-modal__ferie-week-dates">${formatTermShort(courseDateFrom(weekCourse))} do ${formatTermShort(courseDateTo(weekCourse))} · ${weekCourse.days || group.days} dni</span>
            </button>`;
          });
          html += '</div>';
        }
        html += '</div>';
      });
      html += '</div>';
      return html;
    };

    listEl.innerHTML = items.map((item) => {
      if (item.type === 'ferie') return ferieBlock(item.groups);
      const course = item.course;
      const on = !selectedFerie && sameCourse(course, selected);
      return `<button type="button" class="school-course-term-modal__item${on ? ' is-selected' : ''}" data-term-action="course" data-course-id="${escapeHtml(course.id || course.value || '')}">
        <b>${escapeHtml(courseDisplayName(course))}</b>
        <small>${escapeHtml(formatTermLong(courseDateFrom(course), courseDateTo(course), course.days))}</small>
      </button>`;
    }).join('');

    if (selected) {
      summaryEl.innerHTML = `<div class="school-course-term-modal__summary">
        <div class="school-course-term-modal__summary-k">Twój wybór</div>
        <div class="school-course-term-modal__summary-n">${escapeHtml(courseDisplayName(selected))}</div>
        ${selectedFerie && selected.voi ? `<div class="school-course-term-modal__summary-g">${escapeHtml(selected.voi)}</div>` : ''}
        <div class="school-course-term-modal__summary-d">${escapeHtml(formatTermRangeShort(from, to, days))}</div>
      </div>`;
      datesEl.innerHTML = `Wyjazd: <b>${formatTermShort(from)}</b> &nbsp; Powrót: <b>${formatTermShort(to)}</b>`;
    } else {
      summaryEl.innerHTML = '';
      datesEl.innerHTML = '';
    }

    const y = state.calY;
    const m = state.calM;
    const slots = calendarSlotsFromCourses(courses);
    const activeId = selected
      ? (selectedFerie ? `f:${selectedMeta.groupId}:${selectedMeta.week}` : `t:${selected.id || selected.value}`)
      : '';
    const sameGroup = selectedFerie ? selectedMeta.groupId : null;
    const first = new Date(y, m, 1);
    const lead = (first.getDay() + 6) % 7;
    const dim = new Date(y, m + 1, 0).getDate();
    const dimPrev = new Date(y, m, 0).getDate();
    const dow = ['Pon', 'Wto', 'Śro', 'Czw', 'Pią', 'Sob', 'Nie'];
    let cells = dow.map((d) => `<div class="school-course-term-modal__dow">${d}</div>`).join('');
    for (let i = lead - 1; i >= 0; i -= 1) {
      cells += `<div class="school-course-term-modal__day is-out">${dimPrev - i}</div>`;
    }
    for (let d = 1; d <= dim; d += 1) {
      const cur = new Date(y, m, d);
      const curTs = cur.getTime();
      const hits = slots.filter((slot) => {
        const start = parseDate(slot.s);
        const end = parseDate(slot.e);
        return start && end && curTs >= start.getTime() && curTs <= end.getTime();
      });
      // Wybrany termin — zawsze cały zakres dat kursu (nie pojedynczy dzień).
      const activeSlot = activeId ? slots.find((slot) => slot.id === activeId) : null;
      const activeStart = activeSlot ? parseDate(activeSlot.s) : null;
      const activeEnd = activeSlot ? parseDate(activeSlot.e) : null;
      const active = !!(
        activeSlot
        && activeStart
        && activeEnd
        && curTs >= activeStart.getTime()
        && curTs <= activeEnd.getTime()
      );

      const hit = hits.find((slot) => slot.id === activeId)
        || hits.find((slot) => slot.kind === 'ferie' && sameGroup && slot.groupId === sameGroup)
        || hits[0];
      const sameG = !!(hit && !active && hit.kind === 'ferie' && sameGroup && hit.groupId === sameGroup);

      // Zaokrąglenia „bloku” względem slotu, który malujemy (aktywny albo hit).
      const paint = active ? activeSlot : hit;
      const paintStart = paint ? parseDate(paint.s) : null;
      const paintEnd = paint ? parseDate(paint.e) : null;
      const isS = !!(paintStart && curTs === paintStart.getTime());
      const isE = !!(paintEnd && curTs === paintEnd.getTime());
      const isMid = !!(paint && !isS && !isE);

      let cls = 'school-course-term-modal__day';
      if (active) {
        cls += isMid ? ' is-in' : ' is-edge';
      } else if (sameG) {
        cls += ' is-sg';
      } else if (hit) {
        cls += ' is-other';
      }
      if (paint && isS) cls += ' is-rs';
      if (paint && isE) cls += ' is-re';
      if (paint && isS && isE) cls += ' is-single';

      const slotAttr = paint ? ` data-slot-id="${paint.id}"` : '';
      const click = hit && !active ? ' data-term-action="cal-pick"' : '';
      const tag = hit && !active ? 'button' : 'div';
      const type = tag === 'button' ? ' type="button"' : '';
      cells += `<${tag} class="${cls}"${type}${click}${slotAttr}>${d}</${tag}>`;
    }
    const tail = (7 - ((lead + dim) % 7)) % 7;
    for (let i = 1; i <= tail; i += 1) {
      cells += `<div class="school-course-term-modal__day is-out">${i}</div>`;
    }

    delete calendarEl.dataset.hoverId;
    calendarEl.innerHTML = `<div class="school-course-term-modal__cal-head">
      <button type="button" class="school-course-term-modal__cal-nav" data-term-action="cal-go" data-delta="-1" aria-label="Poprzedni miesiąc">‹</button>
      <span>${PL_MONTHS_CAL[m]} ${y}</span>
      <button type="button" class="school-course-term-modal__cal-nav" data-term-action="cal-go" data-delta="1" aria-label="Następny miesiąc">›</button>
    </div>
    <div class="school-course-term-modal__cal-grid">${cells}</div>`;

    legendEl.innerHTML = `<div class="school-course-term-modal__legend">
      <span><i class="is-b"></i> wybrany termin</span>
      ${selectedFerie ? '<span><i class="is-s"></i> drugi tydzień Twoich ferii</span>' : ''}
      <span><i class="is-o"></i> inne terminy, kliknij aby wybrać</span>
    </div>`;
  };

  const findFerieCourse = (groupId, week) => {
    const courses = catalogCoursesForRoot(root);
    return courses.find((course) => {
      if (!isFerieCourse(course)) return false;
      const meta = ferieMetaFromCourse(course);
      return meta.groupId === groupId && meta.week === Number(week);
    }) || null;
  };

  const prefersReducedMotion = () => (
    typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  );

  /** Po rozwinięciu ferii / wyborze regionu — dociągnij nową treść w scrollowalnej liście. */
  const scrollTermListTo = (selector, { block = 'nearest' } = {}) => {
    requestAnimationFrame(() => {
      const el = listEl.querySelector(selector);
      if (!el || typeof el.scrollIntoView !== 'function') return;
      el.scrollIntoView({
        block,
        inline: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    });
  };

  const pickFerie = (groupId, week = 0) => {
    const course = findFerieCourse(groupId, week)
      || catalogCoursesForRoot(root).find((row) => isFerieCourse(row) && ferieMetaFromCourse(row).groupId === groupId);
    if (!course) return;
    state.ferieOpen = true;
    focusCal(courseDateFrom(course));
    applyCourse(course);
    renderModal();
    scrollTermListTo(
      `.school-course-term-modal__ferie-group.is-on .school-course-term-modal__ferie-weeks, .school-course-term-modal__ferie-group.is-on`,
      { block: 'nearest' },
    );
  };

  const openModal = () => {
    const combo = liveTermCombo(root);
    if (!combo) return;
    state.openedSnapshot = captureTermSnapshot();
    state.committing = false;
    const selected = findCourseByCombo(catalogCoursesForRoot(root), combo);
    state.ferieOpen = isFerieCourse(selected);
    focusCal(courseDateFrom(selected));
    renderModal();
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    const selectedEl = listEl.querySelector('.is-selected, .school-course-term-modal__ferie-head.is-on');
    selectedEl?.scrollIntoView({ block: 'nearest' });
    if (state.ferieOpen) {
      scrollTermListTo('.school-course-term-modal__ferie-groups', { block: 'nearest' });
    }
  };

  const closeModal = ({ commit = false } = {}) => {
    if (!commit && state.openedSnapshot && !state.committing) {
      restoreTermSnapshot(state.openedSnapshot);
    }
    state.openedSnapshot = null;
    state.committing = false;
    if (dialog.open) dialog.close();
    else dialog.removeAttribute('open');
  };

  /** Potwierdzenie: jedna podmiana treści + zamknięcie (przeglądanie w modalu jest silent). */
  const confirmModal = () => {
    const combo = liveTermCombo(root);
    if (!combo) {
      closeModal({ commit: true });
      return;
    }
    const unchanged = Boolean(
      state.openedSnapshot
      && state.openedSnapshot.value === (combo.dataset.value || ''),
    );
    if (unchanged) {
      closeModal({ commit: true });
      return;
    }
    state.committing = true;
    state.openedSnapshot = null;
    runWithCourseContentLoading(root, () => {
      // Po swapie hero combo z domknięcia może zginąć — sync bierze żywy węzeł.
      syncSchoolCourseCatalog(root, liveTermCombo(root) || combo);
    }, {
      onDone: () => {
        closeModal({ commit: true });
        // CTA / tytuł / breadcrumb — bez drugiego overlayu.
        // NIE dispatchuj na starym `combo`: po swapie sekcji `hero` jest odpięty od DOM,
        // a listener `school-combo:change` wtedy pomija syncLinks (stale CTA / header).
        root._syncCourseBookingLinks?.();
        root._stickySummaryApi?.sync?.();
        const live = liveTermCombo(root);
        if (live) {
          live.dispatchEvent(new CustomEvent('school-combo:change', {
            bubbles: true,
            detail: { linksOnly: true },
          }));
        }
      },
    });
  };

  // Bubbling z combo (także po swapie — nowe węzły) oraz z „Zmień termin”.
  root.addEventListener('school-term-picker:open', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('.js-school-combo[data-combo="term"], .js-school-course-term')) return;
    if (!comboBelongsToProduct(root, target) && !root.contains(target)) return;
    openModal();
  });

  const escapeSlotSelector = (slotId) => (
    typeof CSS !== 'undefined' && CSS.escape
      ? CSS.escape(slotId)
      : slotId.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  );

  const clearCalendarHover = () => {
    delete calendarEl.dataset.hoverId;
    calendarEl.querySelectorAll('.is-hover-range').forEach((el) => el.classList.remove('is-hover-range'));
  };

  /** Podświetl pełny zakres turnusu w kalendarzu (hover na dniu lub pozycji z listy). */
  const setCalendarHoverSlot = (slotId) => {
    if (!slotId) {
      clearCalendarHover();
      return;
    }
    if (calendarEl.dataset.hoverId === slotId) return;
    calendarEl.dataset.hoverId = slotId;
    const grid = calendarEl.querySelector('.school-course-term-modal__cal-grid');
    if (!grid) return;
    grid.querySelectorAll('.is-hover-range').forEach((el) => el.classList.remove('is-hover-range'));
    const escaped = escapeSlotSelector(slotId);
    grid.querySelectorAll(`.school-course-term-modal__day[data-slot-id="${escaped}"]`).forEach((el) => {
      el.classList.add('is-hover-range');
    });
  };

  // Hover na dowolnym dniu turnusu → podświetl cały zakres (np. 23–26 XII).
  calendarEl.addEventListener('pointerover', (event) => {
    const day = event.target.closest('.school-course-term-modal__day[data-slot-id]');
    if (!day || !calendarEl.contains(day)) return;
    setCalendarHoverSlot(day.getAttribute('data-slot-id') || '');
  });
  calendarEl.addEventListener('pointerleave', clearCalendarHover);

  // Hover na pozycji z listy (kurs / tydzień ferii) → ten sam podgląd zakresu w kalendarzu.
  listEl.addEventListener('pointerover', (event) => {
    const courseBtn = event.target.closest('[data-term-action="course"][data-course-id]');
    if (courseBtn && listEl.contains(courseBtn)) {
      setCalendarHoverSlot(`t:${courseBtn.dataset.courseId}`);
      return;
    }
    const weekBtn = event.target.closest('[data-term-action="ferie-week"][data-group-id]');
    if (weekBtn && listEl.contains(weekBtn)) {
      setCalendarHoverSlot(`f:${weekBtn.dataset.groupId}:${weekBtn.dataset.week}`);
      return;
    }
    const groupBtn = event.target.closest('[data-term-action="ferie-group"][data-group-id]');
    if (groupBtn && listEl.contains(groupBtn)) {
      setCalendarHoverSlot(`f:${groupBtn.dataset.groupId}:0`);
    }
  });
  listEl.addEventListener('pointerleave', clearCalendarHover);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      closeModal();
      return;
    }
    const btn = event.target.closest('[data-term-action]');
    if (!btn || !dialog.contains(btn)) return;
    const action = btn.dataset.termAction;
    if (action === 'course') {
      const courses = catalogCoursesForRoot(root);
      const course = courses.find((row) => (row.id || row.value) === btn.dataset.courseId);
      if (!course) return;
      state.ferieOpen = false;
      focusCal(courseDateFrom(course));
      applyCourse(course);
      renderModal();
      return;
    }
    if (action === 'toggle-ferie') {
      state.ferieOpen = !state.ferieOpen;
      renderModal();
      if (state.ferieOpen) {
        // Lista ma max-height — bez scrolla rozwinięte regiony zostają pod spodem poza viewportem.
        scrollTermListTo('.school-course-term-modal__ferie-group:last-child, .school-course-term-modal__ferie-groups', { block: 'nearest' });
      }
      return;
    }
    if (action === 'ferie-group') {
      pickFerie(btn.dataset.groupId, 0);
      return;
    }
    if (action === 'ferie-week') {
      pickFerie(btn.dataset.groupId, btn.dataset.week);
      return;
    }
    if (action === 'cal-go') {
      let month = state.calM + Number(btn.dataset.delta);
      let year = state.calY;
      if (month < 0) { month = 11; year -= 1; }
      if (month > 11) { month = 0; year += 1; }
      state.calM = month;
      state.calY = year;
      renderModal();
      return;
    }
    if (action === 'cal-pick') {
      const slotId = btn.dataset.slotId || '';
      if (slotId.startsWith('f:')) {
        const parts = slotId.split(':');
        pickFerie(parts[1], parts[2]);
        return;
      }
      if (slotId.startsWith('t:')) {
        const id = slotId.slice(2);
        const course = catalogCoursesForRoot(root).find((row) => (row.id || row.value) === id);
        if (!course) return;
        state.ferieOpen = false;
        focusCal(courseDateFrom(course));
        applyCourse(course);
        renderModal();
      }
    }
  });

  dialog.querySelector('.js-term-modal-close')?.addEventListener('click', () => closeModal());
  dialog.querySelector('.js-term-modal-confirm')?.addEventListener('click', confirmModal);
  // Escape / backdrop — natywne `close`; przywróć snapshot jeśli nie commit.
  dialog.addEventListener('cancel', () => {
    if (!state.committing && state.openedSnapshot) {
      restoreTermSnapshot(state.openedSnapshot);
    }
    state.openedSnapshot = null;
    state.committing = false;
  });

  root._refreshTermPicker = () => {
    const combo = prepareCourseTermCombo(root);
    if (!combo) return;
    const selected = findCourseByCombo(catalogCoursesForRoot(root), combo);
    const name = courseDisplayName(selected);
    if (name) combo.dataset.label = name;
    updateCourseTermTrigger(combo, selected);
    if (dialog.open) renderModal();
  };

  root._refreshTermPicker();
  ensureChangeTermButton(root);
};

const formatCardPrice = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '';
  return `${Math.round(n).toLocaleString('pl-PL')} zł`;
};

const clearSchoolComboSelection = (wrap, placeholder = 'Wybierz pakiet') => {
  if (!wrap) return;
  const valueEl = wrap.querySelector('.js-school-combo-value');
  wrap.dataset.value = '';
  wrap.dataset.label = placeholder;
  delete wrap.dataset.price;
  delete wrap.dataset.bookingUrl;
  delete wrap.dataset.note;
  if (valueEl) {
    valueEl.textContent = placeholder;
    valueEl.classList.add('is-placeholder');
  }
  wrap.querySelectorAll('.js-school-combo-option').forEach((opt) => {
    opt.classList.remove('is-selected');
    opt.setAttribute('aria-selected', 'false');
  });
};

const rebuildSchoolComboOptions = (wrap, options, {
  withMeta = false,
  withNote = false,
  preserveEmpty = false,
  placeholder = '',
} = {}) => {
  const list = wrap?.querySelector('.js-school-combo-list');
  if (!wrap || !list) return;
  const current = wrap.dataset.value || '';
  const emptyLabel = placeholder || wrap.dataset.placeholder || 'Wybierz pakiet';
  list.innerHTML = '';
  options.forEach((opt) => {
    const li = document.createElement('li');
    li.className = 'school-product-page__combo-option js-school-combo-option';
    if (withNote && opt.note) {
      li.classList.add('school-product-page__combo-option--with-note');
    }
    li.setAttribute('role', 'option');
    li.tabIndex = -1;
    li.dataset.value = opt.value || '';
    li.dataset.label = opt.label || '';
    if (opt.note) li.dataset.note = opt.note;
    if (opt.date_from) li.dataset.dateFrom = opt.date_from;
    if (opt.date_to) li.dataset.dateTo = opt.date_to;
    if (opt.days) li.dataset.days = String(opt.days);
    if (opt.price !== undefined && opt.price !== '') li.dataset.price = String(opt.price);
    if (opt.booking_url) li.dataset.bookingUrl = opt.booking_url;
    if (opt.lesson_type) li.dataset.lessonType = opt.lesson_type;
    if (opt.opt_id) li.dataset.optId = opt.opt_id;
    if (opt.destination) li.dataset.destination = opt.destination;
    if (opt.product_url) li.dataset.productUrl = opt.product_url;
    if (opt.is_ferie) li.dataset.isFerie = '1';
    if (withNote) {
      const copy = document.createElement('span');
      copy.className = 'school-product-page__combo-option-copy';
      const label = document.createElement('span');
      label.className = 'school-product-page__combo-option-label';
      label.textContent = opt.label || '';
      copy.appendChild(label);
      if (opt.note) {
        const note = document.createElement('span');
        note.className = 'school-product-page__combo-option-note';
        note.textContent = opt.note;
        copy.appendChild(note);
      }
      li.appendChild(copy);
    } else {
      const label = document.createElement('span');
      label.className = 'school-product-page__combo-option-label';
      label.textContent = opt.label || '';
      li.appendChild(label);
    }
    if (withMeta && opt.days) {
      const meta = document.createElement('span');
      meta.className = 'school-product-page__combo-option-meta';
      meta.textContent = formatTermDaysLabel(opt.days);
      li.appendChild(meta);
    } else if (opt.price !== undefined && opt.price !== '') {
      const priceText = formatCardPrice(opt.price) || String(opt.price);
      if (priceText) {
        const meta = document.createElement('span');
        meta.className = 'school-product-page__combo-option-meta';
        meta.textContent = priceText;
        li.appendChild(meta);
      }
    }
    li.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      selectSchoolComboOption(wrap, li);
    });
    list.appendChild(li);
  });
  const optionsEls = [...list.querySelectorAll('.js-school-combo-option')];
  const prevFrom = wrap.dataset.dateFrom || '';
  const prevTo = wrap.dataset.dateTo || '';
  const prevFerie = wrap.dataset.isFerie === '1' || wrap.dataset.isFerie === 'true';
  const match = current
    ? optionsEls.find((opt) => opt.dataset.value === current)
      || optionsEls.find((opt) => {
        if (!prevFrom || !prevTo) return false;
        if (opt.dataset.dateFrom !== prevFrom || opt.dataset.dateTo !== prevTo) return false;
        const optFerie = opt.dataset.isFerie === '1' || opt.dataset.isFerie === 'true';
        return optFerie === prevFerie;
      })
    : null;
  if (match) {
    selectSchoolComboOption(wrap, match, { silent: true });
    return;
  }
  if (preserveEmpty) {
    clearSchoolComboSelection(wrap, emptyLabel);
    return;
  }
  const first = list.querySelector('.js-school-combo-option');
  if (first) selectSchoolComboOption(wrap, first, { silent: true });
};

/** Kolejność = katalog Carbon (bez sortu ceną). */
const packagesInCatalogOrder = (packages = []) => uniqueCoursePackages(packages);

const packageFeatureLines = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    if (typeof row === 'string' || typeof row === 'number') return String(row).trim();
    if (row && typeof row === 'object') {
      return String(row.text || row.label || row.item || '').trim();
    }
    return '';
  }).filter(Boolean);
};

const fillCoursePackageCardFeatures = (card, pkg) => {
  const features = card.querySelector('.school-product-page__pricing-card-features');
  if (!features) return;

  const includes = packageFeatureLines(pkg.includes);
  const excludes = packageFeatureLines(pkg.excludes);
  const excludedIdx = new Set(
    (Array.isArray(pkg.excluded_includes) ? pkg.excluded_includes : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n)),
  );

  const existingCheck = features.querySelector('.school-product-page__pricing-card-check');
  const checkSrc = existingCheck?.getAttribute('src') || '';
  const existingCancel = features.querySelector('.school-product-page__pricing-card-cancel');
  const cancelSrc = existingCancel?.tagName === 'IMG' ? (existingCancel.getAttribute('src') || '') : '';

  const checkIconHtml = (excluded) => {
    if (excluded) {
      if (cancelSrc) {
        return `<span class="school-product-page__pricing-card-icon" aria-hidden="true"><img class="school-product-page__pricing-card-cancel" src="${cancelSrc}" alt="" width="24" height="24" loading="lazy" decoding="async"></span>`;
      }
      return `<span class="school-product-page__pricing-card-icon" aria-hidden="true"><svg class="school-product-page__pricing-card-cancel" width="24" height="24" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M11 11l10 10M21 11 11 21" stroke="#8FACF7" stroke-width="1.5" stroke-linecap="round"/></svg></span>`;
    }
    if (checkSrc) {
      return `<img class="school-product-page__pricing-card-check" src="${checkSrc}" alt="" width="32" height="32" loading="lazy" decoding="async" aria-hidden="true">`;
    }
    return '';
  };

  const includeItems = includes.map((text, index) => {
    const excluded = excludedIdx.has(index);
    return `<li${excluded ? ' class="school-product-page__pricing-card-list-item--excluded"' : ''}>${checkIconHtml(excluded)}<span class="school-product-page__pricing-card-list-text"></span></li>`;
  }).join('');

  const excludeItems = excludes.map(() => (
    `<li>${checkIconHtml(true)}<span class="school-product-page__pricing-card-list-text"></span></li>`
  )).join('');

  features.innerHTML = [
    '<p class="school-product-page__pricing-card-subtitle">Zawiera:</p>',
    '<ul class="school-product-page__pricing-card-list js-expandable-list" data-expandable-visible="1" data-expandable-mobile="1" data-expandable-more="Pokaż szczegóły" data-expandable-less="Ukryj szczegóły">',
    includeItems,
    '</ul>',
    excludes.length
      ? [
        '<div class="js-expandable-extra expandable-list__extra">',
        '<p class="school-product-page__pricing-card-subtitle school-product-page__pricing-card-subtitle--excludes">Cena nie zawiera:</p>',
        `<ul class="school-product-page__pricing-card-list school-product-page__pricing-card-list--excluded">${excludeItems}</ul>`,
        '</div>',
      ].join('')
      : '',
  ].join('');

  const includeTexts = features.querySelectorAll('.school-product-page__pricing-card-list:not(.school-product-page__pricing-card-list--excluded) .school-product-page__pricing-card-list-text');
  includes.forEach((text, i) => {
    if (includeTexts[i]) includeTexts[i].textContent = text;
  });
  const excludeTexts = features.querySelectorAll('.school-product-page__pricing-card-list--excluded .school-product-page__pricing-card-list-text');
  excludes.forEach((text, i) => {
    if (excludeTexts[i]) excludeTexts[i].textContent = text;
  });

  initExpandableLists(features);
};

const setPackageCardCtaLabel = (cta, label) => {
  if (!(cta instanceof HTMLElement)) return;
  let box = cta.querySelector('.school-product-page__pricing-card-checkbox');
  if (!box) {
    box = document.createElement('span');
    box.className = 'school-product-page__pricing-card-checkbox';
    box.setAttribute('aria-hidden', 'true');
  }
  cta.replaceChildren(box, document.createTextNode(String(label || 'Wybierz')));
};

const ensurePackageCardHeaderCta = (card) => {
  if (!(card instanceof HTMLElement)) return null;
  let cta = card.querySelector('.js-school-course-cta');
  const head = card.querySelector('.school-product-page__pricing-card-head');
  const name = card.querySelector('.school-product-page__pricing-card-name');
  if (!head || !name) return cta;

  let nameRow = card.querySelector('.school-product-page__pricing-card-name-row');
  if (!nameRow) {
    nameRow = document.createElement('div');
    nameRow.className = 'school-product-page__pricing-card-name-row';
    name.parentNode?.insertBefore(nameRow, name);
  }

  let titleGroup = nameRow.querySelector('.school-product-page__pricing-card-title-group');
  if (!titleGroup) {
    titleGroup = document.createElement('div');
    titleGroup.className = 'school-product-page__pricing-card-title-group';
    nameRow.insertBefore(titleGroup, nameRow.firstChild);
  }
  if (!titleGroup.contains(name)) titleGroup.appendChild(name);
  const badge = head.querySelector('.school-product-page__pricing-card-badge');
  if (badge && !titleGroup.contains(badge)) titleGroup.appendChild(badge);

  if (!cta) {
    cta = document.createElement('a');
    cta.href = '#';
    cta.className = 'school-product-page__pricing-card-select js-school-course-cta';
    cta.setAttribute('role', 'button');
    cta.dataset.packagePick = '1';
    cta.setAttribute('aria-pressed', 'false');
    setPackageCardCtaLabel(cta, 'Wybierz');
  }

  cta.classList.remove('btn', 'btn--block', 'btn--primary', 'btn--no-arrow');
  cta.classList.add('school-product-page__pricing-card-select');
  if (!cta.querySelector('.school-product-page__pricing-card-checkbox')) {
    setPackageCardCtaLabel(cta, (cta.textContent || 'Wybierz').trim() || 'Wybierz');
  }
  if (!nameRow.contains(cta)) nameRow.appendChild(cta);
  return cta;
};

const fillCoursePackageCard = (card, pkg) => {
  card.hidden = false;
  card.dataset.packageId = pkg.id || '';
  const nameEl = card.querySelector('.school-product-page__pricing-card-name');
  const priceEl = card.querySelector('.school-product-page__pricing-card-price');
  const noteEl = card.querySelector('.school-product-page__pricing-card-note');
  const cta = ensurePackageCardHeaderCta(card);
  if (nameEl) nameEl.textContent = pkg.name || pkg.select_label || pkg.label || '';
  if (priceEl) {
    const labeled = String(pkg.price || '').trim();
    priceEl.textContent = labeled || formatCardPrice(pkg.price_value ?? 0) || '0 zł';
  }
  if (noteEl) noteEl.textContent = pkg.note || '';
  const featured = pkg.variant === 'featured'
    || Boolean(pkg.badge)
    || pkg.id === 'podstawowy';
  card.classList.toggle('school-product-page__pricing-card--featured', featured);
  card.classList.toggle('school-product-page__pricing-card--standard', !featured);
  const badgeEl = card.querySelector('.school-product-page__pricing-card-badge');
  const badge = String(pkg.badge || (featured ? 'Najczęściej wybierany' : '')).trim();
  if (badgeEl) {
    badgeEl.textContent = badge || 'Najczęściej wybierany';
    badgeEl.hidden = !badge;
  }
  fillCoursePackageCardFeatures(card, pkg);
  if (cta) {
    // Start: wybór. Po selectcie syncPackageCards ustawi „Wybrano” (bez przejścia do rezerwacji).
    cta.setAttribute('href', '#');
    cta.setAttribute('role', 'button');
    cta.dataset.packagePick = '1';
    setPackageCardCtaLabel(cta, 'Wybierz');
    cta.setAttribute('aria-pressed', 'false');
  }
};

const minPackagePrice = (packages = []) => {
  let min = null;
  packages.forEach((pkg) => {
    const raw = pkg?.price_value ?? pkg?.price;
    if (raw === '' || raw === null || raw === undefined) return;
    const price = Number(raw);
    if (!Number.isFinite(price) || price < 0) return;
    if (min === null || price < min) min = price;
  });
  return min;
};

const formatOdPrice = (value) => {
  const formatted = formatCardPrice(value);
  return formatted ? `od ${formatted}` : '';
};

const minPackagePriceFromCombo = (combo) => {
  if (!combo) return null;
  let min = null;
  combo.querySelectorAll('.js-school-combo-option').forEach((opt) => {
    if (!Object.prototype.hasOwnProperty.call(opt.dataset, 'price')) return;
    const raw = opt.dataset.price;
    if (raw === '') return;
    const price = Number(raw);
    if (!Number.isFinite(price) || price < 0) return;
    if (min === null || price < min) min = price;
  });
  return min;
};

const packagesSectionEl = (root) => (
  root.querySelector('#section-cennik, .js-school-packages, .js-school-course-pricing-cards')
  || root.querySelector('[id*="cennik"]')
);

/** Jak OfferSubNav.stickyOffset — header + sticky switcher + gap (nie scrollIntoView). */
const packagesScrollOffsetPx = () => {
  const header = document.querySelector('.header, .site-header, #masthead, [data-sticky-header]');
  const headerPart = header instanceof HTMLElement ? header.getBoundingClientRect().height : 72;
  const nav = document.querySelector('.js-offer-sub-nav, .offer-sub-nav');
  const subNavH = nav instanceof HTMLElement ? Math.round(nav.getBoundingClientRect().height) : 0;
  const gapRaw = getComputedStyle(document.documentElement).getPropertyValue('--scroll-gap').trim();
  const gap = Number.parseFloat(gapRaw);
  return Math.round(headerPart + subNavH + (Number.isFinite(gap) ? gap : 32));
};

const highlightPackagesSection = (root) => {
  const section = packagesSectionEl(root)?.closest('section, .school-product-page__section')
    || packagesSectionEl(root);
  if (!section) return null;
  section.classList.add('is-package-spotlight');
  window.setTimeout(() => section.classList.remove('is-package-spotlight'), 2200);
  return section;
};

const scrollToPackagesSection = (root, { closeDrawer } = {}) => {
  closeDrawer?.({ immediate: true });
  // Po zamknięciu drawera layout się przelicza — drugi rAF przed pomiarem.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const section = highlightPackagesSection(root);
      if (!section) return;
      const top = window.scrollY + section.getBoundingClientRect().top - packagesScrollOffsetPx();
      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth',
      });
    });
  });
};

const showPackageRequiredTip = (anchor) => {
  if (!anchor) return;
  let tip = anchor.parentElement?.querySelector('.js-school-package-tip');
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'school-product-page__package-tip js-school-package-tip';
    tip.setAttribute('role', 'status');
    tip.textContent = 'Najpierw wybierz pakiet';
    anchor.parentElement?.appendChild(tip);
  }
  tip.hidden = false;
  tip.classList.add('is-visible');
  window.clearTimeout(tip._hideTimer);
  tip._hideTimer = window.setTimeout(() => {
    tip.classList.remove('is-visible');
    tip.hidden = true;
  }, 2600);
};

const uniqueCoursePackages = (packages = []) => {
  const seen = new Set();
  const out = [];
  packages.forEach((pkg) => {
    if (!pkg || typeof pkg !== 'object') return;
    const id = String(pkg.id || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(pkg);
  });
  return out;
};

/** Kolejność kolumn checków w HTML harmonogramu (SSR / paczki). */
const SCHEDULE_PKG_SLOT_ORDER = ['ekonomiczny', 'podstawowy'];

/**
 * Pokazuje w harmonogramie tylko kolumny pakietów dostępnych na kursie
 * (np. Włochy = sam „podstawowy” → jedna kolumna).
 */
const syncSchedulePackageColumns = (root, packages) => {
  const tables = root.querySelectorAll('.school-product-page__schedule-table');
  if (!tables.length) return;

  const unique = uniqueCoursePackages(packages);
  const byId = new Map(unique.map((pkg) => [String(pkg.id || ''), pkg]));
  const knownIds = SCHEDULE_PKG_SLOT_ORDER.filter((id) => byId.has(id));
  // Brak mapowania id → nie chowaj (zachowaj obie kolumny z HTML).
  const visibleIds = knownIds.length
    ? new Set(knownIds)
    : (unique.length ? null : new Set(SCHEDULE_PKG_SLOT_ORDER));

  tables.forEach((table) => {
    const ths = [...table.querySelectorAll('.school-product-page__schedule-th-pkg')];
    ths.forEach((th, index) => {
      const slotId = th.dataset.schedulePkg || SCHEDULE_PKG_SLOT_ORDER[index] || '';
      if (slotId) th.dataset.schedulePkg = slotId;
      const show = !visibleIds || visibleIds.has(slotId);
      th.hidden = !show;
      th.style.display = show ? '' : 'none';
      if (show && byId.has(slotId)) {
        const pkg = byId.get(slotId);
        const label = th.querySelector('.school-product-page__schedule-th-pkg-label');
        const name = String(pkg?.name || pkg?.label || '').trim();
        if (label && name) label.textContent = name;
      }
    });

    table.querySelectorAll('tbody tr').forEach((tr) => {
      const tds = [...tr.querySelectorAll('.school-product-page__schedule-td-check')];
      tds.forEach((td, index) => {
        const slotId = td.dataset.schedulePkg || SCHEDULE_PKG_SLOT_ORDER[index] || '';
        if (slotId) td.dataset.schedulePkg = slotId;
        const show = !visibleIds || visibleIds.has(slotId);
        td.hidden = !show;
        td.style.display = show ? '' : 'none';
        if (show && byId.has(slotId)) {
          const pkg = byId.get(slotId);
          const label = td.querySelector('.school-product-page__schedule-check-label');
          const name = String(pkg?.name || pkg?.label || '').trim();
          if (label && name) label.textContent = name;
        }
      });
    });
  });
};

/** Wszystkie dni harmonogramu domyślnie zamknięte (także po swapie paczki z legacy `open`). */
const collapseScheduleDays = (root) => {
  root?.querySelectorAll?.('.school-product-page__schedule-day[open], details.school-product-page__schedule-day')
    .forEach((el) => {
      if (el instanceof HTMLDetailsElement) el.open = false;
    });
};


const syncCoursePackageCards = (root, packages) => {
  const wrap = root.querySelector('.js-school-course-pricing-cards');
  const unique = packagesInCatalogOrder(packages);
  if (!wrap || !unique.length) return;
  const template = wrap.querySelector('.js-school-package-card');
  if (!template) return;

  const byId = new Map();
  wrap.querySelectorAll('.js-school-package-card').forEach((card) => {
    const id = card.dataset.packageId || '';
    if (id && !byId.has(id)) byId.set(id, card);
  });

  const used = new Set();
  const ordered = [];
  unique.forEach((pkg) => {
    let card = byId.get(pkg.id);
    if (!card) {
      card = template.cloneNode(true);
    }
    fillCoursePackageCard(card, pkg);
    used.add(card);
    ordered.push(card);
  });

  wrap.querySelectorAll('.js-school-package-card').forEach((card) => {
    if (!used.has(card)) card.remove();
  });

  // Karty zawsze na początku wrapa — nie za ewentualnymi InnerBlocks / innymi węzłami.
  ordered.forEach((card, index) => {
    const ref = wrap.children[index] || null;
    if (ref !== card) {
      wrap.insertBefore(card, ref);
    }
  });
};

const syncSchoolCourseCatalog = (root, sourceCombo = null) => {
  const catalog = parseSchoolCourseCatalog(root);
  if (!catalog.length) return false;

  const locationCombo = queryProduct(root, '.js-school-course-location');
  const termCombo = queryProduct(root, '.js-school-combo[data-combo="term"]');
  const packageCombo = queryProduct(root, '.js-school-combo[data-combo="package"]');
  const locationKey = locationCombo?.dataset.value
    || root.dataset.location
    || resolveLocationKey(effectiveDestinations(root));
  const slope = findCatalogSlope(catalog, locationKey, effectiveDestinations(root));
  if (!slope) return false;

  const courses = upcomingCoursesOnly(Array.isArray(slope.courses) ? slope.courses : []);
  // Drawer klonuje combo — porównuj data-combo / class, nie identity węzła.
  const isLocationChange = !sourceCombo
    || sourceCombo === locationCombo
    || sourceCombo.dataset?.combo === 'location'
    || sourceCombo.classList?.contains('js-school-course-location');
  const isPackageChange = Boolean(
    sourceCombo
    && (
      sourceCombo === packageCombo
      || sourceCombo.dataset?.combo === 'package'
      || sourceCombo.classList?.contains('js-school-course-package')
    ),
  );
  // Pakiet: tylko CTA/cennik — bez swapu hero (inaczej combosy wracają do SSR).
  if (isPackageChange) {
    return true;
  }

  const termOptionValue = (course) => (
    course?.value
    || (course?.date_from && course?.date_to ? `${course.date_from}_${course.date_to}` : '')
    || course?.id
    || ''
  );
  const termOptions = courses.map((course) => ({
    value: termOptionValue(course),
    label: courseDisplayName(course) || course.title || course.label || '',
    date_from: course.date_from || '',
    date_to: course.date_to || '',
    days: course.days || 0,
    is_ferie: isFerieCourse(course),
    course_id: course.id || '',
  }));

  if (termCombo && courses.length && isLocationChange) {
    rebuildSchoolComboOptions(termCombo, termOptions, { withMeta: true });
  }

  const selectedTermValue = termCombo?.dataset.value || '';
  const course = courses.find((row) => (
    row.value === selectedTermValue
    || row.id === selectedTermValue
    || termOptionValue(row) === selectedTermValue
  )) || courses[0];
  const packages = Array.isArray(course?.packages) ? course.packages : [];
  const selectedPackageId = packageCombo?.dataset.value || '';
  const selectedTermId = termOptionValue(course);
  let packageIdToRestore = selectedPackageId;

  if (packageCombo && packages.length) {
    // Preferuj aktualne value przed rebuild (po zmianie terminu zachowaj pakiet jeśli nadal jest).
    if (selectedPackageId) packageCombo.dataset.value = selectedPackageId;
    rebuildSchoolComboOptions(packageCombo, packages.map((pkg) => ({
      value: pkg.id || '',
      label: pkg.select_label || pkg.label || pkg.name || '',
      note: pkg.note || pkg.desc || '',
      price: (pkg.price_value ?? pkg.price ?? ''),
      booking_url: pkg.booking_url || '',
    })), {
      withNote: true,
      preserveEmpty: true,
      placeholder: packageCombo.dataset.placeholder || 'Wybierz pakiet',
    });
    // Po rebuild: tylko pakiet faktycznie dostępny w nowym terminie (nie przywracaj stale id).
    packageIdToRestore = packageCombo.dataset.value || '';
  } else if (packageCombo && selectedPackageId && !packages.length) {
    clearSchoolComboSelection(packageCombo, packageCombo.dataset.placeholder || 'Wybierz pakiet');
    packageIdToRestore = '';
  }

  // Najpierw HTML z paczki (m.in. #section-cennik / sidebar w sekcji hero), potem karty.
  const courseDiscipline = getCourseDiscipline(root) || lessonDisciplineFromUrl() || 'ski';
  const packKey = `${contentKeyFromCourse(course) || ''}|${slope.id || locationKey || ''}|${courseDiscipline}`;
  const samePack = root.dataset.termContentPackKey === packKey;
  if (!samePack) {
    syncTermContentSections(root, course, slope.id || locationKey || '');
    // Paczka `hero` to cały aside — po innerHTML trzeba od nowa zbindować term picker
    // i przywrócić wybory (swap wraca do SSR z paczki).
    initCourseTermPicker(root);
    restoreCourseCombosAfterContentSwap(root, {
      locationKey: slope.id || locationKey || '',
      courses,
      termOptions,
      selectedTermId,
      packages,
      selectedPackageId: packageIdToRestore,
    });
    root.dataset.termContentPackKey = packKey;
  }

  syncCoursePackageCards(root, packages);
  syncSchedulePackageColumns(root, packages);
  collapseScheduleDays(root);
  // Swap galerii / sekcji w js-term-content zrywa kotwice OfferSubNav i IO — odśwież.
  document.dispatchEvent(new CustomEvent('hski:term-content-updated'));
  // Lightbox po idle — nie blokuj overlayu „Ładowanie oferty…”.
  const scheduleLightbox = () => ensureProductGalleryLightbox();
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(scheduleLightbox, { timeout: 600 });
  } else {
    window.setTimeout(scheduleLightbox, 0);
  }
  const minPrice = minPackagePrice(packages);
  if (minPrice === null || minPrice === undefined) {
    delete root.dataset.minPackagePrice;
  } else {
    root.dataset.minPackagePrice = String(minPrice);
  }
  if (slope.offer_id) root.dataset.offerId = String(slope.offer_id);
  if (slope.page_id) root.dataset.productPageId = String(slope.page_id);
  if (slope.id) root.dataset.location = slope.id;
  if (slope.audience) root.dataset.audience = slope.audience;
  if (course?.id) root.dataset.course = course.id;
  {
    const label = courseDisplayName(course);
    if (label) root.dataset.courseLabel = label;
  }
  syncCourseDisciplines(root, course);
  root._refreshTermPicker?.();
  return true;
};

/** Po syncTermContentSections (hero/aside) przywróć stok / termin / pakiet. */
const restoreCourseCombosAfterContentSwap = (root, {
  locationKey = '',
  courses = [],
  termOptions = [],
  selectedTermId = '',
  packages = [],
  selectedPackageId = '',
} = {}) => {
  const locationCombo = queryProduct(root, '.js-school-course-location');
  const termCombo = queryProduct(root, '.js-school-combo[data-combo="term"]');
  const packageCombo = queryProduct(root, '.js-school-combo[data-combo="package"]');

  if (locationCombo && locationKey) {
    const locOpt = [...locationCombo.querySelectorAll('.js-school-combo-option')].find((opt) => (
      opt.dataset.value === locationKey
      || opt.dataset.destination === locationKey
    ));
    if (locOpt) selectSchoolComboOption(locationCombo, locOpt, { silent: true });
  }

  if (termCombo && termOptions.length) {
    if (selectedTermId) termCombo.dataset.value = selectedTermId;
    const courseRow = courses.find((row) => (
      row.value === selectedTermId
      || row.id === selectedTermId
      || (row.date_from && row.date_to && `${row.date_from}_${row.date_to}` === selectedTermId)
    ));
    if (courseRow?.date_from) termCombo.dataset.dateFrom = courseRow.date_from;
    if (courseRow?.date_to) termCombo.dataset.dateTo = courseRow.date_to;
    rebuildSchoolComboOptions(termCombo, termOptions, { withMeta: true });
  }

  if (packageCombo && packages.length) {
    if (selectedPackageId) packageCombo.dataset.value = selectedPackageId;
    rebuildSchoolComboOptions(packageCombo, packages.map((pkg) => ({
      value: pkg.id || '',
      label: pkg.select_label || pkg.label || pkg.name || '',
      note: pkg.note || pkg.desc || '',
      price: (pkg.price_value ?? pkg.price ?? ''),
      booking_url: pkg.booking_url || '',
    })), {
      withNote: true,
      preserveEmpty: true,
      placeholder: packageCombo.dataset.placeholder || 'Wybierz pakiet',
    });
  }
};

/** Combosy w sidebarze są w paczce `hero` — syncTermContentSections podmienia DOM.
 *  Delegacja na root, żeby nie ginęły listenery przy każdym swapie stoku/kursu. */
const initSchoolCombos = (root) => {
  if (!root || root.dataset.schoolCombosDelegated === '1') return;
  root.dataset.schoolCombosDelegated = '1';

  const allCombos = () => [...root.querySelectorAll('.js-school-combo')];

  root.addEventListener('click', (event) => {
    const option = event.target.closest?.('.js-school-combo-option');
    if (option && root.contains(option)) {
      const wrap = option.closest('.js-school-combo');
      if (!wrap || !root.contains(wrap)) return;
      event.preventDefault();
      event.stopPropagation();
      selectSchoolComboOption(wrap, option);
      return;
    }

    const trigger = event.target.closest?.('.js-school-combo-trigger');
    if (!trigger || !root.contains(trigger)) return;
    const wrap = trigger.closest('.js-school-combo');
    if (!wrap || !root.contains(wrap)) return;

    event.preventDefault();
    event.stopPropagation();
    if (wrap.classList.contains('js-school-course-term') && wrap.dataset.termPicker === '1') {
      wrap.dispatchEvent(new CustomEvent('school-term-picker:open', { bubbles: true }));
      return;
    }
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    allCombos().forEach((other) => {
      if (other !== wrap) closeSchoolCombo(other);
    });
    if (isOpen) closeSchoolCombo(wrap);
    else openSchoolCombo(wrap);
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('.js-school-combo')) return;
    allCombos().forEach((wrap) => closeSchoolCombo(wrap));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    allCombos().forEach((wrap) => closeSchoolCombo(wrap));
  });
};

/** Karta cennika / CTA pakietu — zaznacza pakiet w combo (bez przejścia do rezerwacji). */
const selectCoursePackageById = (root, packageId) => {
  const id = String(packageId || '').trim();
  if (!id || !root) return false;

  const packageCombo = queryProduct(root, '.js-school-combo[data-combo="package"], .js-school-course-package');
  const card = root.querySelector(
    `.js-school-package-card[data-package-id="${CSS.escape(id)}"], .school-product-page__pricing-card[data-package-id="${CSS.escape(id)}"]`,
  );
  const cardLabel = card?.querySelector('.school-product-page__pricing-card-name')?.textContent?.trim() || id;

  if (packageCombo) {
    let opt = [...packageCombo.querySelectorAll('.js-school-combo-option')]
      .find((option) => option.dataset.value === id);
    // Karta może mieć pakiet spoza aktualnej listy combo (SSR vs katalog) — dopisz opcję.
    if (!opt) {
      const list = packageCombo.querySelector('.js-school-combo-list');
      if (list) {
        opt = document.createElement('li');
        opt.className = 'school-product-page__combo-option js-school-combo-option';
        opt.setAttribute('role', 'option');
        opt.tabIndex = -1;
        opt.dataset.value = id;
        opt.dataset.label = cardLabel;
        const labelEl = document.createElement('span');
        labelEl.className = 'school-product-page__combo-option-label';
        labelEl.textContent = cardLabel;
        opt.appendChild(labelEl);
        list.appendChild(opt);
      }
    }
    if (opt) {
      selectSchoolComboOption(packageCombo, opt);
      // Pingwin: belka ma zostać zamknięta — wybór pakietu tylko zaznacza kartę.
      if (!isPingwinProductPage(root)) {
        window.requestAnimationFrame(() => root._stickySummaryApi?.openDrawer?.());
      }
      return true;
    }
  }
  const packageSelect = root.querySelector('select.js-school-package-select');
  if (packageSelect) {
    const hasOpt = [...packageSelect.options].some((option) => option.value === id);
    if (hasOpt) {
      packageSelect.value = id;
      packageSelect.dispatchEvent(new Event('change', { bubbles: true }));
      if (!isPingwinProductPage(root)) {
        window.requestAnimationFrame(() => root._stickySummaryApi?.openDrawer?.());
      }
      return true;
    }
  }
  // Ostateczny fallback: zaznacz kartę wizualnie nawet bez combo.
  if (card) {
    root.querySelectorAll(
      '.js-school-package-card[data-package-id], .school-product-page__pricing-card[data-package-id]',
    ).forEach((el) => {
      const on = el.dataset.packageId === id;
      el.classList.toggle('is-selected', on);
      const cta = ensurePackageCardHeaderCta(el);
      if (!cta) return;
      setPackageCardCtaLabel(cta, on ? 'Wybrano' : 'Wybierz');
      cta.setAttribute('aria-pressed', String(on));
      cta.dataset.packagePick = '1';
      cta.setAttribute('href', '#');
      cta.setAttribute('role', 'button');
    });
    root.dataset.packageSelected = '1';
    return true;
  }
  return false;
};

const initCourseBookingSync = (root) => {
  if (root.querySelector('.js-school-lesson-widget')) return;
  if (root.dataset.courseSyncBound === '1') return;
  root.dataset.courseSyncBound = '1';

  root.querySelectorAll('.js-school-package-card, .school-product-page__pricing-card[data-package-id]').forEach((card) => {
    const cta = ensurePackageCardHeaderCta(card);
    if (!cta) return;
    cta.setAttribute('href', '#');
    cta.setAttribute('role', 'button');
    cta.dataset.packagePick = '1';
    const label = (cta.textContent || '').trim();
    if (!/^(wybierz|wybrano|wybrany)$/i.test(label)) {
      setPackageCardCtaLabel(cta, 'Wybierz');
    } else if (/^wybrany$/i.test(label)) {
      setPackageCardCtaLabel(cta, 'Wybrano');
    } else {
      setPackageCardCtaLabel(cta, label || 'Wybierz');
    }
  });

  const sportInputs = root.querySelectorAll('.school-product-page__sport-toggle input[type="radio"]');
  // Legacy native selects (fallback) — rzadko, nie w paczce hero.
  const termSelect = root.querySelector('select.js-school-term-select');
  const packageSelect = root.querySelector('select.js-school-package-select');
  const ageSelect = root.querySelector('select.js-school-age-select');

  const livePackageCombo = () => queryProduct(root, '.js-school-combo[data-combo="package"], .js-school-course-package');
  const liveTermCombo = () => queryProduct(root, '.js-school-combo[data-combo="term"]');
  const liveAgeCombo = () => queryProduct(root, '.js-school-combo[data-combo="age"]');
  const liveLocationCombo = () => queryProduct(root, '.js-school-course-location');
  const livePrimaryCta = () => (
    queryProduct(root, '.school-product-page__sidebar-booking .js-school-course-cta')
    || queryProduct(root, '.school-product-page__sidebar .js-school-course-cta')
  );

  const syncPackageCards = (packageId, _bookingHref = '') => {
    const cards = root.querySelectorAll(
      '.js-school-package-card[data-package-id], .school-product-page__pricing-card[data-package-id]',
    );
    if (!cards.length) return;
    cards.forEach((card) => {
      const on = packageId !== '' && card.dataset.packageId === packageId;
      card.classList.toggle('is-selected', on);
      const cta = ensurePackageCardHeaderCta(card);
      if (!cta) return;
      // Tylko wybór pakietu — rezerwacja zostaje w sidebarze / belce, nie na CTA karty.
      cta.setAttribute('href', '#');
      cta.setAttribute('role', 'button');
      cta.dataset.packagePick = '1';
      setPackageCardCtaLabel(cta, on ? 'Wybrano' : 'Wybierz');
      cta.setAttribute('aria-pressed', String(on));
    });
  };

  const comboLabelIfSelected = (combo) => {
    const value = String(combo?.dataset.value || '').trim();
    const label = String(combo?.dataset.label || '').trim();
    // Placeholder „Wybierz” (puste value) nie może iść do H1 / belki.
    if (!value || !label || /^wybierz\b/i.test(label)) return '';
    return label;
  };

  const syncLinks = () => {
    // Combosy / CTA siedzą w paczce `hero` — po swapie stare referencje są martwe.
    const locationCombo = liveLocationCombo();
    const termCombo = liveTermCombo();
    const packageCombo = livePackageCombo();
    const ageCombo = liveAgeCombo();
    const primaryCta = livePrimaryCta();

    const discipline = getCourseDiscipline(root);
    const dateFrom = termCombo?.dataset.dateFrom || termSelect?.selectedOptions?.[0]?.dataset.dateFrom || '';
    const dateTo = termCombo?.dataset.dateTo || termSelect?.selectedOptions?.[0]?.dataset.dateTo || '';
    const courseTypeCombo = queryProduct(root, '.js-school-combo[data-combo="course_type"]');
    const selectedCourse = findCourseByCombo(catalogCoursesForRoot(root), termCombo);
    const coursePackages = Array.isArray(selectedCourse?.packages) ? selectedCourse.packages : [];
    // Instruktor: tytuł = rodzaj kursu. Pingwin/dorośli: tytuł = wybrany termin (nazwa kursu).
    // Preferuj courseDisplayName z katalogu (np. „Kurs feryjny”), nie długi title z regionami.
    const termCourseLabel = courseDisplayName(selectedCourse)
      || comboLabelIfSelected(termCombo)
      || termSelect?.selectedOptions?.[0]?.dataset.title
      || '';
    if (termCourseLabel && termCombo) termCombo.dataset.label = termCourseLabel;
    const courseLabel = comboLabelIfSelected(courseTypeCombo) || termCourseLabel;
    const ageGroup = ageCombo?.dataset.value || ageSelect?.value || '';
    let packageId = packageCombo?.dataset.value || packageSelect?.value || '';
    let catalogPkg = packageId
      ? coursePackages.find((pkg) => String(pkg?.id || '') === String(packageId))
      : null;

    // Pakiet z poprzedniego terminu niedostępny w nowym — wyczyść wybór (bez starego CTA).
    if (packageId && coursePackages.length && !catalogPkg) {
      if (packageCombo) {
        clearSchoolComboSelection(packageCombo, packageCombo.dataset.placeholder || 'Wybierz pakiet');
      }
      if (packageSelect) packageSelect.value = '';
      packageId = '';
      catalogPkg = null;
    }

    // Źródło prawdy po zmianie terminu: katalog bieżącego kursu (świeże booking_url / cena).
    if (catalogPkg && packageCombo) {
      const nextUrl = String(catalogPkg.booking_url || '');
      const nextPrice = catalogPkg.price_value ?? catalogPkg.price;
      const nextLabel = String(
        catalogPkg.select_label || catalogPkg.label || catalogPkg.name || '',
      ).trim();
      if (nextUrl) packageCombo.dataset.bookingUrl = nextUrl;
      else delete packageCombo.dataset.bookingUrl;
      if (nextPrice !== undefined && nextPrice !== null && nextPrice !== '') {
        packageCombo.dataset.price = String(nextPrice);
      }
      if (nextLabel) {
        packageCombo.dataset.label = nextLabel;
        const valueEl = packageCombo.querySelector('.js-school-combo-value');
        if (valueEl && packageCombo.dataset.value) {
          valueEl.textContent = nextLabel;
          valueEl.classList.remove('is-placeholder');
        }
      }
    }

    const packageLabel = (catalogPkg
      && String(catalogPkg.select_label || catalogPkg.label || catalogPkg.name || '').trim())
      || packageCombo?.dataset.label
      || packageSelect?.selectedOptions?.[0]?.dataset.label
      || '';
    const packagePrice = (catalogPkg && (catalogPkg.price_value ?? catalogPkg.price) !== undefined
      && (catalogPkg.price_value ?? catalogPkg.price) !== null
      && (catalogPkg.price_value ?? catalogPkg.price) !== '')
      ? String(catalogPkg.price_value ?? catalogPkg.price)
      : (packageCombo?.dataset.price || packageSelect?.selectedOptions?.[0]?.dataset.price || '');
    const packageBookingUrl = String(catalogPkg?.booking_url || '')
      || packageCombo?.dataset.bookingUrl
      || packageSelect?.selectedOptions?.[0]?.dataset.bookingUrl
      || '';
    const locationKey = locationCombo?.dataset.value
      || root.dataset.location
      || resolveLocationKey(effectiveDestinations(root))
      || '';
    if (locationKey) root.dataset.location = locationKey;
    // Trzymaj dataset.course zsynchronizowany z wybranym terminem (param `course` w CTA).
    if (selectedCourse?.id) root.dataset.course = String(selectedCourse.id);
    {
      const label = courseDisplayName(selectedCourse);
      if (label) root.dataset.courseLabel = label;
    }

    const titleEl = root.querySelector('.school-product-page__page-title');
    if (titleEl && courseLabel) {
      titleEl.textContent = courseLabel;
    }
    // Crumb: Pingwin = Dzieci › Klub Pingwina › Kurs X; inne = update current.
    // Instruktor: termCourseLabel puste przy course_type — zostawia SSR.
    if (termCourseLabel) {
      syncCourseBreadcrumb(root, termCourseLabel);
    }
    const subtitleEl = root.querySelector('.school-product-page__page-subtitle');
    if (subtitleEl) {
      const days = termCombo?.dataset.days || termSelect?.selectedOptions?.[0]?.dataset.days || '';
      const termText = formatTermLong(dateFrom, dateTo, days);
      if (termText) subtitleEl.textContent = termText;
    }

    const sidebarTitles = [
      ...root.querySelectorAll('.school-product-page__sidebar-title'),
      ...(productSidebarDrawer(root)?.querySelectorAll('.school-product-page__sidebar-title') || []),
    ];
    if (sidebarTitles.length && courseLabel) {
      sidebarTitles.forEach((sidebarTitle) => {
        delete sidebarTitle.dataset.sidebarTitleBase;
        sidebarTitle.textContent = courseLabel;
      });
    }

    const hasPackage = Boolean(packageId);
    if (primaryCta && hasPackage && packageBookingUrl) {
      primaryCta.href = packageBookingUrl;
    }

    root.dataset.packageSelected = hasPackage ? '1' : '0';
    if (primaryCta) {
      if (!primaryCta.dataset.bookingLabel) {
        primaryCta.dataset.bookingLabel = 'Zarezerwuj teraz';
      } else if (/^(wybierz pakiet|zamów lekcje|zarezerwuj)$/i.test(primaryCta.dataset.bookingLabel)) {
        primaryCta.dataset.bookingLabel = 'Zarezerwuj teraz';
      }
      primaryCta.textContent = hasPackage
        ? 'Zarezerwuj teraz'
        : 'Wybierz pakiet';
      primaryCta.classList.toggle('is-muted', !hasPackage);
      primaryCta.classList.toggle('is-pick-package', !hasPackage);
      if (!hasPackage) {
        primaryCta.setAttribute('aria-disabled', 'true');
        // Nie zostawiaj href z poprzedniego kursu/ceny — CTA ma wymusić wybór pakietu.
        primaryCta.setAttribute('href', '#');
      } else {
        primaryCta.removeAttribute('aria-disabled');
      }
    }

    const highlight = queryProduct(root, '.school-product-page__price-highlight:not(.js-school-lesson-price)');
    if (highlight) {
      if (hasPackage) {
        const formatted = formatCardPrice(packagePrice === '' || packagePrice == null ? NaN : packagePrice)
          || (packagePrice === '0' || Number(packagePrice) === 0 ? '0 zł' : '');
        if (formatted) {
          highlight.textContent = formatted;
          highlight.dataset.fromPrice = formatted;
        }
      } else {
        const fromDs = root.dataset.minPackagePrice;
        const minPrice = (fromDs !== undefined && fromDs !== '')
          ? Number(fromDs)
          : minPackagePriceFromCombo(packageCombo);
        if (minPrice !== null && minPrice !== undefined && Number.isFinite(minPrice) && minPrice >= 0) {
          const od = formatOdPrice(minPrice) || `od ${Math.round(minPrice).toLocaleString('pl-PL')} zł`;
          highlight.textContent = od;
          highlight.dataset.fromPrice = formatCardPrice(minPrice) || `${Math.round(minPrice).toLocaleString('pl-PL')} zł`;
        } else {
          // Brak cen w katalogu — nie trzymaj starego SSR „od 950”.
          highlight.textContent = 'od 0 zł';
          highlight.dataset.fromPrice = '0 zł';
        }
      }
    }

    const bookingLinks = [
      ...root.querySelectorAll('a[href]'),
      ...(productSidebarDrawer(root)?.querySelectorAll('a[href]') || []),
    ];
    bookingLinks.forEach((link) => {
      // Karty pakietów nie prowadzą do rezerwacji — tylko wybór.
      if (link.closest('.js-school-package-card, .school-product-page__pricing-card')) return;
      if (link.dataset.packagePick === '1') return;
      const href = link.getAttribute('href') || '';
      if (!COURSE_BOOKING_HREF.test(href)) return;
      let next = setCourseLinkParam(href, 'discipline', discipline);
      if (dateFrom) next = setCourseLinkParam(next, 'date_from', dateFrom);
      if (dateTo) next = setCourseLinkParam(next, 'date_to', dateTo);
      if (courseLabel) next = setCourseLinkParam(next, 'course_label', courseLabel);
      if (ageGroup) next = setCourseLinkParam(next, 'age_group', ageGroup);
      if (packageId) {
        next = setCourseLinkParam(next, 'package', packageId);
        if (packageLabel) next = setCourseLinkParam(next, 'package_label', packageLabel);
        if (packagePrice) next = setCourseLinkParam(next, 'price', packagePrice);
      }
      if (locationKey) next = setCourseLinkParam(next, 'location', locationKey);
      const courseId = root.dataset.course || '';
      if (courseId) next = setCourseLinkParam(next, 'course', courseId);
      const offerId = root.dataset.offerId || '';
      if (offerId) next = setCourseLinkParam(next, 'offer', offerId);
      const productPageId = root.dataset.productPageId || '';
      if (productPageId) next = setCourseLinkParam(next, 'page', productPageId);
      const audience = root.dataset.audience || '';
      if (audience) next = setCourseLinkParam(next, 'audience', audience);
      link.href = next;
    });

    // Karty pakietów: href z pełnymi parametrami (po syncu sidebara).
    let cardBookingHref = '';
    if (packageId) {
      const fromPrimary = (primaryCta?.getAttribute('href') || '').trim();
      if (fromPrimary && fromPrimary !== '#' && COURSE_BOOKING_HREF.test(fromPrimary)) {
        cardBookingHref = fromPrimary;
      } else if (packageBookingUrl) {
        let next = packageBookingUrl;
        next = setCourseLinkParam(next, 'discipline', discipline);
        if (dateFrom) next = setCourseLinkParam(next, 'date_from', dateFrom);
        if (dateTo) next = setCourseLinkParam(next, 'date_to', dateTo);
        if (courseLabel) next = setCourseLinkParam(next, 'course_label', courseLabel);
        if (ageGroup) next = setCourseLinkParam(next, 'age_group', ageGroup);
        next = setCourseLinkParam(next, 'package', packageId);
        if (packageLabel) next = setCourseLinkParam(next, 'package_label', packageLabel);
        if (packagePrice) next = setCourseLinkParam(next, 'price', packagePrice);
        if (locationKey) next = setCourseLinkParam(next, 'location', locationKey);
        const courseId = root.dataset.course || '';
        if (courseId) next = setCourseLinkParam(next, 'course', courseId);
        cardBookingHref = next;
      }
    }
    syncPackageCards(packageId, cardBookingHref);
  };

  const hydrateFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const termCombo = liveTermCombo();
    const packageCombo = livePackageCombo();
    const ageCombo = liveAgeCombo();
    if (termCombo) {
      const from = params.get('date_from') || '';
      const to = params.get('date_to') || '';
      if (from && to) {
        const match = [...termCombo.querySelectorAll('.js-school-combo-option')].find((opt) => (
          opt.dataset.value === `${from}_${to}`
          || (opt.dataset.dateFrom === from && opt.dataset.dateTo === to)
        ));
        if (match) selectSchoolComboOption(termCombo, match);
      }
    }
    if (packageCombo) {
      const pkg = params.get('package') || '';
      if (pkg) {
        const match = [...packageCombo.querySelectorAll('.js-school-combo-option')].find((opt) => opt.dataset.value === pkg);
        if (match) selectSchoolComboOption(packageCombo, match);
      }
    }
    if (ageCombo) {
      const age = params.get('age_group') || '';
      if (age) {
        const match = [...ageCombo.querySelectorAll('.js-school-combo-option')].find((opt) => opt.dataset.value === age);
        if (match) selectSchoolComboOption(ageCombo, match);
      }
    }
  };

  hydrateCourseLocationFromUrl(root);
  hydrateFromUrl();
  // discipline z URL / meta strony → zaznacz radio przed pierwszym sync paczki.
  {
    const disc = resolveLessonDiscipline(root);
    if (disc === 'snowboard') {
      const sb = queryProduct(root, '.school-product-page__sport-toggle input[name="sport"][value="snowboard"]')
        || root.querySelector('.school-product-page__sport-toggle input[value="snowboard"]');
      if (sb instanceof HTMLInputElement && !sb.disabled) {
        sb.checked = true;
        syncSportBadge(root, 'snowboard');
      }
      root.dataset.lessonDiscipline = 'snowboard';
    }
  }
  hydrateOfertyBreadcrumbs(root);
  syncSchoolCourseCatalog(root, queryProduct(root, '.js-school-course-location'));
  initCourseTermPicker(root);
  sportInputs.forEach((input) => input.addEventListener('change', () => {
    // Pingwin / kursy: discipline w URL + swap paczki (jak stok/termin). Lekcje = reload osobno.
    const sportValue = input.value === 'snowboard' ? 'snowboard' : 'narty';
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('sport');
      if (sportValue === 'snowboard') url.searchParams.set('discipline', 'snowboard');
      else url.searchParams.delete('discipline');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      /* ignore */
    }
    syncSportBadge(root, sportValue);
    delete root.dataset.termContentPackKey;
    runWithCourseContentLoading(root, () => {
      syncSchoolCourseCatalog(root, queryProduct(root, '.js-school-course-location'));
      syncLinks();
    });
  }));
  // Mobile drawer przenosi sidebar poza root — event musi iść przez document.
  document.addEventListener('school-combo:change', (event) => {
    const combo = event.target?.closest?.('.js-school-combo') || event.target;
    // linksOnly: po swapie hero event może iść ze świeżego combo; sync CTA i tak jest idempotentny.
    if (event.detail?.linksOnly) {
      if (comboBelongsToProduct(root, combo) || event.target === root) {
        syncLinks();
      }
      return;
    }
    if (!comboBelongsToProduct(root, combo)) return;
    if (applyCourseLocationChange(root, combo)) return;
    // Przeglądanie terminów w otwartym modalu jest silent; safety net gdyby ktoś odpalił change.
    const termModal = root.querySelector('.js-school-course-term-modal');
    const isTermCombo = combo.matches?.('.js-school-combo[data-combo="term"]')
      || combo.classList?.contains('js-school-course-term');
    if (termModal?.open && isTermCombo) {
      return;
    }
    const run = () => {
      syncSchoolCourseCatalog(root, combo);
      syncLinks();
    };
    // Pakiet = tylko CTA/cennik w sidebarze — bez overlayu na treści.
    const isPackageOnly = combo.matches?.('.js-school-combo[data-combo="package"]')
      || combo.classList?.contains('js-school-course-package');
    if (isPackageOnly) run();
    else runWithCourseContentLoading(root, run);
  });
  termSelect?.addEventListener('change', syncLinks);
  packageSelect?.addEventListener('change', syncLinks);
  ageSelect?.addEventListener('change', syncLinks);

  const onCourseCtaClick = (event) => {
    const link = event.target?.closest?.('.js-school-course-cta');
    if (!link) return;
    const packageCard = link.closest('.js-school-package-card, .school-product-page__pricing-card[data-package-id]');
    if (packageCard || link.dataset.packagePick === '1') {
      // CTA na karcie pakietu nigdy nie prowadzi do rezerwacji — tylko wybór („Wybierz” → „Wybrano”).
      event.preventDefault();
      event.stopPropagation();
      if (
        packageCard?.classList.contains('is-selected')
        && link.dataset.packagePick === '1'
        && /^(wybrano|wybrany)$/i.test((link.textContent || '').replace(/\s+/g, ' ').trim())
      ) {
        return;
      }
      const fromHref = (() => {
        try {
          return new URL(link.href, window.location.origin).searchParams.get('package') || '';
        } catch {
          return '';
        }
      })();
      const packageId = packageCard?.dataset.packageId || fromHref || '';
      selectCoursePackageById(root, packageId);
      return;
    }
    const inRoot = root.contains(link);
    const inDrawer = Boolean(productSidebarDrawer(root)?.contains(link));
    if (!inRoot && !inDrawer) return;
    const packageId = livePackageCombo()?.dataset.value || packageSelect?.value || '';
    if (packageId) return;
    event.preventDefault();
    showPackageRequiredTip(link);
    scrollToPackagesSection(root, {
      closeDrawer: root._stickySummaryApi?.closeDrawer,
    });
  };
  document.addEventListener('click', onCourseCtaClick, true);

  const onPackageCardClick = (event) => {
    const card = event.target?.closest?.('.js-school-package-card[data-package-id], .school-product-page__pricing-card[data-package-id]');
    if (!card || !root.contains(card)) return;
    if (event.target.closest('a, button')) return;
    event.preventDefault();
    selectCoursePackageById(root, card.dataset.packageId || '');
  };
  root.addEventListener('click', onPackageCardClick);

  root._syncCourseBookingLinks = syncLinks;
  syncLinks();
};

const initBadgeSwitcher = (root) => {
  if (!root) return;

  /** Po syncTermContentSections (zmiana kursu) HTML odznak jest nowy — trzeba zbindować od nowa. */
  const bind = () => {
    const wrap = root.querySelector('.js-school-badges');
    if (!wrap || wrap.dataset.badgesBound === '1') return;

    const tabs = [...wrap.querySelectorAll('.js-school-badge-tab')];
    const panes = [...wrap.querySelectorAll('.js-school-badge-pane')];
    if (!tabs.length || !panes.length) return;

    wrap.dataset.badgesBound = '1';

    const ids = [...new Set(panes.map((pane) => pane.dataset.badgeId).filter(Boolean))];
    const prevBtn = wrap.querySelector('.js-school-badge-prev');
    const nextBtn = wrap.querySelector('.js-school-badge-next');

    const scrollBadgeTabIntoRow = (rowSel, tabSel) => {
      const row = wrap.querySelector(rowSel);
      const active = wrap.querySelector(tabSel);
      if (!active || !row || !row.contains(active) || row.scrollWidth <= row.clientWidth) return;
      // Tylko scroll poziomy wiersza — scrollIntoView przewija też dokument (skok do Program/Odznaki).
      const rowBox = row.getBoundingClientRect();
      const tabBox = active.getBoundingClientRect();
      const pad = 16;
      if (tabBox.left < rowBox.left + pad) {
        row.scrollBy({ left: tabBox.left - rowBox.left - pad, behavior: 'smooth' });
      } else if (tabBox.right > rowBox.right - pad) {
        row.scrollBy({ left: tabBox.right - rowBox.right + pad, behavior: 'smooth' });
      }
    };

    const activate = (id, { scrollRow = true } = {}) => {
      tabs.forEach((tab) => {
        const on = tab.dataset.badgeId === id;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-pressed', String(on));
        tab.setAttribute('aria-selected', String(on));
      });
      panes.forEach((pane) => {
        const on = pane.dataset.badgeId === id;
        pane.classList.toggle('is-active', on);
        pane.hidden = !on;
      });

      if (scrollRow) {
        scrollBadgeTabIntoRow(
          '.school-product-page__badge-icons',
          '.school-product-page__badge-icons .js-school-badge-tab.is-active'
        );
        scrollBadgeTabIntoRow(
          '.school-product-page__badge-tabs',
          '.school-product-page__badge-tabs .js-school-badge-tab.is-active'
        );
      }

      const index = ids.indexOf(id);
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index < 0 || index >= ids.length - 1;
    };

    const currentId = () => {
      const active = panes.find((pane) => !pane.hidden && pane.classList.contains('is-active'))
        || panes.find((pane) => !pane.hidden)
        || panes[0];
      return active?.dataset.badgeId || ids[0];
    };

    const step = (delta) => {
      const index = ids.indexOf(currentId());
      if (index < 0) return;
      const next = ids[index + delta];
      if (next) activate(next);
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab.dataset.badgeId));
    });
    prevBtn?.addEventListener('click', () => step(-1));
    nextBtn?.addEventListener('click', () => step(1));

    activate(currentId(), { scrollRow: false });
  };

  bind();
  if (root.dataset.badgeSwitcherListen === '1') return;
  root.dataset.badgeSwitcherListen = '1';
  document.addEventListener('hski:term-content-updated', bind);
};

const initInstructorSteps = (root) => {
  const wrap = root.querySelector('.js-instructor-steps');
  if (!wrap || wrap.dataset.stepsBound === '1') return;
  wrap.dataset.stepsBound = '1';

  const tabs = [...wrap.querySelectorAll('.js-instructor-step-tab')];
  const panes = [...wrap.querySelectorAll('.js-instructor-step-pane')];
  if (!tabs.length || !panes.length) return;

  const ids = [...new Set(panes.map((pane) => pane.dataset.stepId).filter(Boolean))];
  const prevBtn = wrap.querySelector('.js-instructor-step-prev');
  const nextBtn = wrap.querySelector('.js-instructor-step-next');

  const activate = (id) => {
    tabs.forEach((tab) => {
      const on = tab.dataset.stepId === id;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', String(on));
    });
    panes.forEach((pane) => {
      const on = pane.dataset.stepId === id;
      pane.classList.toggle('is-active', on);
      pane.hidden = !on;
    });

    const index = ids.indexOf(id);
    if (prevBtn) prevBtn.disabled = index <= 0;
    if (nextBtn) nextBtn.disabled = index < 0 || index >= ids.length - 1;
  };

  const currentId = () => {
    const active = panes.find((pane) => !pane.hidden && pane.classList.contains('is-active'))
      || panes.find((pane) => !pane.hidden)
      || panes[0];
    return active?.dataset.stepId || ids[0];
  };

  const step = (delta) => {
    const index = ids.indexOf(currentId());
    if (index < 0) return;
    const next = ids[index + delta];
    if (next) activate(next);
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => activate(tab.dataset.stepId));
  });
  prevBtn?.addEventListener('click', () => step(-1));
  nextBtn?.addEventListener('click', () => step(1));

  activate(currentId());
};

const selectLessonPeople = (widget, people, { force = false } = {}) => {
  const wrap = widget.querySelector('.js-school-lesson-people.js-school-combo, .js-school-combo.js-school-lesson-people');
  if (wrap) {
    const match = [...wrap.querySelectorAll('.js-school-combo-option')].find(
      (opt) => opt.dataset.value === String(people),
    );
    if (match && (force || wrap.dataset.value !== String(people))) {
      selectSchoolComboOption(wrap, match);
    }
    return;
  }
  const select = widget.querySelector('select.js-school-lesson-people');
  if (select && (force || select.value !== String(people))) {
    select.value = String(people);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

const selectLessonHours = (widget, hours, lessonType, { force = false } = {}) => {
  const wrap = widget.querySelector('.js-school-lesson-hours.js-school-combo, .js-school-combo.js-school-lesson-hours');
  if (wrap) {
    const match = [...wrap.querySelectorAll('.js-school-combo-option')].find((opt) => (
      opt.dataset.value === String(hours)
      && (opt.dataset.lessonType || 'single') === lessonType
    ));
    if (
      match
      && (
        force
        || wrap.dataset.value !== String(hours)
        || (wrap.dataset.lessonType || 'single') !== lessonType
        || (wrap.dataset.optId || '') !== (match.dataset.optId || '')
      )
    ) {
      selectSchoolComboOption(wrap, match);
    }
    return;
  }
  const select = widget.querySelector('select.js-school-lesson-hours');
  if (!select) return;
  const opt = [...select.options].find((option) => (
    option.value === String(hours)
    && (option.dataset.lessonType || 'single') === lessonType
  ));
  if (opt && (force || select.value !== opt.value)) {
    select.value = opt.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

const applyLessonPackagePick = (widget, hours, lessonType, people) => {
  const root = widget?.closest?.('.js-school-product')
    || document.querySelector('.js-school-product');
  const live = liveLessonWidget(root) || widget;
  if (!live) return;
  setLessonPackagePicked(root, true);
  // force: nawet gdy combo już ma te wartości — odśwież etykiety/optId i wywołaj sync ceny.
  selectLessonHours(live, hours, lessonType, { force: true });
  selectLessonPeople(live, people, { force: true });
  if (typeof root?._syncLessonWidget === 'function') {
    root._syncLessonWidget();
  } else if (root) {
    syncPriceCards(root, getWidgetState(live), root.dataset.bookingPath || '/rezerwacja-lekcji/');
  }
  window.requestAnimationFrame(() => {
    root?._stickySummaryApi?.sync?.();
  });
};

const initLessonPackageCards = (root, widget) => {
  const live = liveLessonWidget(root) || widget;
  root.querySelectorAll('.js-school-price-card').forEach((card) => {
    if (card.dataset.packageBound === '1') return;
    card.dataset.packageBound = '1';

    card.querySelectorAll('.js-school-package-tabs [data-people]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        applyLessonPackagePick(
          liveLessonWidget(root) || live,
          Number(card.dataset.hours),
          card.dataset.lessonType || 'single',
          Number(btn.dataset.people),
        );
      });
    });

    // Wybór pakietu tylko przez taby osób (bez przycisku „Wybierz” na karcie).
    card.addEventListener('click', (event) => {
      if (event.target.closest('a, button')) return;
      const current = liveLessonWidget(root) || live;
      const people = current ? getWidgetState(current).people : 1;
      applyLessonPackagePick(
        current,
        Number(card.dataset.hours),
        card.dataset.lessonType || 'single',
        people,
      );
    });
  });
};


const formatStickyMoney = (value) => {
  const n = Number(String(value ?? '').replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return '';
  return `${n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
};

const formatStickyCoursePrice = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return '';
  return `${Math.round(n).toLocaleString('pl-PL')} zł`;
};

const initSchoolProductSummaryBar = (root) => {
  const sidebar = root.querySelector('.school-product-page__sidebar--block')
    || root.querySelector('.school-product-page__hero-grid .school-product-page__sidebar')
    || root.querySelector('.school-product-page__sidebar');
  if (!sidebar) return;

  const isLead = Boolean(sidebar.querySelector('.school-product-page__sidebar-booking--lead'));
  const isLesson = Boolean(sidebar.classList.contains('js-school-lesson-widget')
    || sidebar.querySelector('.js-school-lesson-widget')
    || root.querySelector('.js-school-lesson-widget'));
  const hasPackageCombo = Boolean(
    root.querySelector('.js-school-combo[data-combo="package"], .js-school-course-package')
  );

  const sourceCta = () => (
    queryProduct(root, '.school-product-page__sidebar-booking .js-school-course-cta')
    || queryProduct(root, '.school-product-page__sidebar .js-school-course-cta')
    || queryProduct(root, '.js-school-lesson-cta')
    || sidebar.querySelector('.js-school-lesson-cta')
    || sidebar.querySelector('.js-school-course-cta')
    || root.querySelector('.js-instructor-lead-open:not(.school-instructor-lead-fab)')
    || root.querySelector('.js-instructor-lead-open')
  );

  const packageSelected = () => {
    const pkg = queryProduct(root, '.js-school-combo[data-combo="package"], .js-school-course-package');
    return Boolean(pkg?.dataset.value);
  };

  const lessonPriceParts = () => {
    const lesson = sidebar.querySelector('.js-school-lesson-price')
      || root.querySelector('.js-school-lesson-price');
    const raw = lesson?.textContent.replace(/\s+/g, ' ').trim() || '';
    return {
      caption: 'Razem',
      value: raw.replace(/^od\s+/i, '').trim(),
    };
  };

  const resolveProductSidebar = () => (
    root.querySelector('.school-product-page__sidebar--block')
    || root.querySelector('.school-product-page__hero-grid .school-product-page__sidebar')
    || root.querySelector('.school-product-page__sidebar')
  );

  // Belka mobile eSky wszędzie (Pingwin / lekcje / instruktor / lead).
  // Sidebar zostaje w treści na mobile (karta) + klon w sheet.
  const api = initStickySummaryBar({
    root,
    sidebar,
    getSidebar: resolveProductSidebar,
    variant: 'course-pick',
    keepSidebarInline: true,
    autoOpenAtBottom: !isLead,
    keepBadgesInDrawer: false,
    pageClass: 'has-sticky-summary',
    bodyClass: 'has-sticky-summary-bar',
    drawerLabel: isLead ? 'Szczegóły oferty' : 'Szczegóły rezerwacji',
    getTotalText: () => {
      if (isLead) {
        const location = sidebar.querySelector('.school-product-page__location')
          ?.textContent.replace(/\s+/g, ' ').trim();
        if (location) return location;
        return sidebar.querySelector('.school-product-page__sidebar-title')
          ?.textContent.replace(/\s+/g, ' ').trim()
          || root.querySelector('.school-product-page__page-title')
            ?.textContent.replace(/\s+/g, ' ').trim()
          || '';
      }
      if (isLesson) return lessonPriceParts().value;
      if (hasPackageCombo) {
        const pkg = queryProduct(root, '.js-school-combo[data-combo="package"], .js-school-course-package');
        if (pkg?.dataset.value) {
          return formatStickyCoursePrice(pkg.dataset.price);
        }
        const fromDs = root.dataset.minPackagePrice;
        const min = (fromDs !== undefined && fromDs !== '')
          ? Number(fromDs)
          : minPackagePriceFromCombo(pkg);
        if (min === null || min === undefined || !Number.isFinite(min) || min < 0) {
          return '0 zł';
        }
        return formatStickyCoursePrice(min);
      }
      const lesson = sidebar.querySelector('.js-school-lesson-price');
      const lessonText = lesson?.textContent.replace(/\s+/g, ' ').trim() || '';
      if (lessonText) return lessonText;
      const pkg = queryProduct(root, '.js-school-combo[data-combo="package"]');
      const pkgPrice = formatStickyMoney(pkg?.dataset.price);
      if (pkgPrice) return pkgPrice;
      const highlight = sidebar.querySelector('.school-product-page__price-highlight');
      return highlight?.textContent.replace(/\s+/g, ' ').trim() || '';
    },
    getPriceCaption: () => {
      if (isLead) return '';
      if (isLesson) return lessonPriceParts().caption;
      if (hasPackageCombo) {
        return packageSelected() ? 'Razem' : 'Od';
      }
      return 'Od';
    },
    getLeadSummary: () => {
      const title = sidebar.querySelector('.school-product-page__sidebar-title')
        ?.textContent.replace(/\s+/g, ' ').trim()
        || root.querySelector('.school-product-page__page-title')
          ?.textContent.replace(/\s+/g, ' ').trim()
        || 'Zapytaj o rezerwację';
      return {
        title,
        note: 'W sprawie opłaty skontaktujemy się z Tobą',
      };
    },
    getCta: () => {
      if (hasPackageCombo && !packageSelected()) {
        return {
          text: 'Wybierz pakiet',
          href: '#section-cennik',
          isButton: true,
          pickPackage: true,
        };
      }
      if (isLesson && !isLessonPackagePicked(root)) {
        return {
          text: 'Wybierz pakiet',
          href: '#section-cennik',
          isButton: true,
          pickPackage: true,
        };
      }
      const cta = sourceCta();
      if (!cta) {
        return {
          text: isLead ? 'Rezerwuj miejsce' : (isLesson || hasPackageCombo ? 'Zarezerwuj teraz' : 'Przejdź dalej'),
          href: '#',
          isButton: true,
        };
      }
      const isButton = cta.tagName === 'BUTTON' || cta.classList.contains('js-instructor-lead-open');
      const bookingHref = (cta.getAttribute('href') || '').trim();
      const bookingText = (cta.dataset.bookingLabel || cta.textContent || '').replace(/\s+/g, ' ').trim()
        || (isLead ? 'Rezerwuj miejsce' : 'Zarezerwuj teraz');
      // Pakiet wybrany — belka prowadzi do rezerwacji (nie „Wybierz pakiet”).
      if (hasPackageCombo && packageSelected()) {
        return {
          text: 'Zarezerwuj teraz',
          href: bookingHref && bookingHref !== '#' ? bookingHref : '#',
          isButton: false,
          pickPackage: false,
        };
      }
      if (isLesson && isLessonPackagePicked(root)) {
        return {
          text: 'Zarezerwuj teraz',
          href: bookingHref && bookingHref !== '#' ? bookingHref : '#',
          isButton: false,
          pickPackage: false,
        };
      }
      return {
        text: bookingText,
        href: bookingHref || '#',
        isButton: isButton || isLead,
        muted: false,
      };
    },
    onCtaClick: (event) => {
      if (hasPackageCombo && !packageSelected()) {
        event.preventDefault();
        scrollToPackagesSection(root, { closeDrawer: api.closeDrawer });
        return;
      }
      if (isLesson && !isLessonPackagePicked(root)) {
        event.preventDefault();
        scrollToPackagesSection(root, { closeDrawer: api.closeDrawer });
        return;
      }
      const cta = sourceCta();
      if (!cta) {
        event.preventDefault();
        return;
      }
      if (cta.tagName === 'BUTTON' || cta.classList.contains('js-instructor-lead-open') || isLead) {
        event.preventDefault();
        // Modal nad drawerem — zamknij sheet, potem otwórz formularz zapytania.
        api.closeDrawer({ immediate: true });
        window.requestAnimationFrame(() => cta.click());
        return;
      }
      // Link do rezerwacji — zamknij sheet i przejdź (belka ma ten sam href).
      const href = (cta.getAttribute('href') || '').trim();
      if (href && href !== '#' && !href.startsWith('#')) {
        api.closeDrawer?.({ immediate: true });
      }
    },
  });

  root._stickySummaryApi = api;

  // Paczka `hero` podmienia aside — trzymaj observer na żywym węźle.
  let observedSidebar = sidebar;
  const observer = new MutationObserver(() => {
    api.sync();
    const live = resolveProductSidebar();
    if (live && live !== observedSidebar) {
      observedSidebar = live;
      observer.disconnect();
      observer.observe(live, { subtree: true, childList: true, characterData: true, attributes: true });
    }
  });
  observer.observe(sidebar, { subtree: true, childList: true, characterData: true, attributes: true });
  document.addEventListener('school-combo:change', (event) => {
    const combo = event.target?.closest?.('.js-school-combo') || event.target;
    if (!comboBelongsToProduct(root, combo)) return;
    api.sync();
    // Wybór pakietu z selecta — otwórz sheet (mobile), poza Klubem Pingwina.
    if (
      combo?.dataset?.combo === 'package'
      && String(combo.dataset.value || '').trim()
      && !isPingwinProductPage(root)
    ) {
      api.openDrawer?.();
    }
  });
};

const initSchoolProduct = () => {
  const root = document.querySelector('.js-school-product');
  if (!root) return;

  if (root.querySelector('.js-school-lesson-widget')) {
    ensureLessonDisciplineScrollTop();
    root.dataset.lessonDiscipline = resolveLessonDiscipline(root);
  }

  const packagesSection = root.querySelector('.js-school-packages[data-lesson-pricing]');
  const pricingFromDom = packagesSection?.dataset.lessonPricing;
  if (pricingFromDom) {
    try {
      const parsed = JSON.parse(pricingFromDom);
      if (parsed && typeof parsed === 'object') {
        Object.assign(LESSON_PRICING, parsed);
      }
    } catch (error) {
      // fallback — LESSON_PRICING z modułu
    }
  }
  const catalog = parseLessonCatalog(root.dataset.schoolLessonCatalog || '');
  if (catalog) setLessonCatalog(catalog);

  const widget = root.querySelector('.js-school-lesson-widget');
  initSchoolCombos(root);
  hydrateProductSearchMeta(root);
  // Badge Dzieci/Dorośli: lekcje (type[]) + kurs instruktora (type[] / referrer / menu).
  hydrateAudienceFromUrl(root);

  if (widget) {
    hydrateLessonLocationFromUrl(widget, root);
    hydrateLessonPeopleFromUrl(widget);
    initLessonWidget(root, widget);
  }

  // Po ewentualnym swapie hero (paczka treści) widget jest nowy — zbindować taby osób.
  initLessonPackageCards(root, liveLessonWidget(root));
  window.addEventListener('resize', () => {
    const live = liveLessonWidget(root);
    if (!live) return;
    syncPriceCards(root, getWidgetState(live), root.dataset.bookingPath || '/rezerwacja-lekcji/');
  });

  initCourseBookingSync(root);
  hydrateInstructorFromUrl(root);
  initBadgeSwitcher(root);
  initInstructorSteps(root);
  initInstructorLead(root);
  initSchoolProductSummaryBar(root);

  // Po starcie synców (content-loading może już wisieć) — zdejmij boot SSR.
  if (typeof window.__hskiClearSchoolBoot === 'function') {
    window.__hskiClearSchoolBoot();
  } else {
    root.classList.remove('is-school-booting');
    if (!root.classList.contains('is-course-content-loading')) {
      root.removeAttribute('aria-busy');
    }
    root.querySelector('.js-school-product-boot-loading')?.setAttribute('aria-hidden', 'true');
  }
};

const initInstructorLead = (root) => {
  const dialog = root.querySelector('.js-instructor-lead-dialog');
  const fab = root.querySelector('.js-instructor-lead-fab');
  const formView = dialog?.querySelector('.js-instructor-lead-form-view');
  const successView = dialog?.querySelector('.js-instructor-lead-success');
  const mq = window.matchMedia('(max-width: 991px)');

  const summaryMq = window.matchMedia('(max-width: 1023px)');
  const syncFab = () => {
    if (!fab) return;
    // Belka eSky przejmuje CTA — FAB tylko gdy belki nie ma (desktop).
    fab.hidden = !mq.matches || summaryMq.matches;
  };
  syncFab();
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', syncFab);
    summaryMq.addEventListener('change', syncFab);
  } else if (typeof mq.addListener === 'function') {
    mq.addListener(syncFab);
    summaryMq.addListener(syncFab);
  }

  const showFormView = () => {
    if (formView) formView.hidden = false;
    if (successView) successView.hidden = true;
    dialog?.classList.remove('is-success');
    root.querySelectorAll('.js-instructor-lead-form').forEach((form) => {
      const body = form.querySelector('.js-instructor-lead-form-body');
      const sidebarSuccess = form.querySelector('.js-instructor-lead-sidebar-success');
      if (body) body.hidden = false;
      if (sidebarSuccess) sidebarSuccess.hidden = true;
      form.classList.remove('is-success');
    });
    if (dialog) {
      dialog.setAttribute(
        'aria-labelledby',
        formView && !formView.hidden ? 'instructor-lead-modal-title' : 'instructor-lead-success-title',
      );
    }
  };

  const showDialogSuccessView = () => {
    if (formView) formView.hidden = true;
    if (successView) {
      successView.hidden = false;
      dialog?.classList.add('is-success');
      dialog?.setAttribute('aria-labelledby', 'instructor-lead-success-title');
      return true;
    }
    return false;
  };

  const showSidebarSuccessView = (form) => {
    const body = form.querySelector('.js-instructor-lead-form-body');
    const sidebarSuccess = form.querySelector('.js-instructor-lead-sidebar-success');
    if (!sidebarSuccess) return false;
    if (body) body.hidden = true;
    sidebarSuccess.hidden = false;
    form.classList.add('is-success');
    const title = sidebarSuccess.querySelector('.school-instructor-lead-success__title');
    if (title?.id) {
      title.focus?.();
    }
    return true;
  };

  const openDialog = (event) => {
    if (event) event.preventDefault();
    if (!dialog || typeof dialog.showModal !== 'function') {
      const sidebar = root.querySelector('#rezerwuj-miejsce');
      sidebar?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    showFormView();
    dialog.showModal();
  };

  const closeDialog = () => {
    if (dialog?.open) dialog.close();
    showFormView();
  };

  root.querySelectorAll('.js-instructor-lead-open').forEach((el) => {
    el.addEventListener('click', openDialog);
  });
  fab?.addEventListener('click', openDialog);
  root.querySelectorAll('.js-instructor-lead-close').forEach((el) => {
    el.addEventListener('click', closeDialog);
  });
  // CF7 może podmienić HTML formularza — domykaj też przez delegację.
  dialog?.addEventListener('click', (event) => {
    if (event.target?.closest?.('.js-instructor-lead-close')) {
      closeDialog();
    }
  });
  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog();
  });
  dialog?.addEventListener('close', showFormView);

  // CF7 w modalu akademii/tyczek — po mailu pokaż ten sam success view co Booking REST.
  document.addEventListener('wpcf7mailsent', (event) => {
    const form = event.target;
    if (!(form instanceof Element) || !dialog?.contains(form)) return;
    showDialogSuccessView();
  });

  root.querySelectorAll('.js-instructor-lead-form').forEach((form) => {
    if (form.dataset.bound === '1') return;
    form.dataset.bound = '1';

    const leadType = form.dataset.leadType || 'instructor_lead';
    const isContactLead = leadType === 'academy_lead' || leadType === 'poles_lead';
    const courseLabel = form.dataset.courseLabel || 'POL-SKI Pomocnik Instruktora i Demonstrator';
    const resumeUrl = form.dataset.resumeUrl || '/kurs-na-instruktora/#rezerwuj-miejsce';

    const collectLeadPayload = () => {
      const courseTypeCombo = form.querySelector('[data-combo="course_type"]');
      const termCombo = form.querySelector('[data-combo="term"]');
      return {
        type: leadType,
        contact: {
          first_name: form.querySelector('[name="first_name"]')?.value?.trim() || '',
          last_name: form.querySelector('[name="last_name"]')?.value?.trim() || '',
          email: form.querySelector('[name="email"]')?.value?.trim() || '',
          phone: form.querySelector('[name="phone"]')?.value?.trim() || '',
          terms: true,
        },
        details: {
          course_label: courseLabel,
          course_type: courseTypeCombo?.dataset.value || '',
          course_type_label: courseTypeCombo?.dataset.label || '',
          term: termCombo?.dataset.label || termCombo?.dataset.value || '',
          date_label: termCombo?.dataset.label || '',
          date_from: termCombo?.dataset.dateFrom || '',
          date_to: termCombo?.dataset.dateTo || '',
          message: form.querySelector('[name="message"]')?.value?.trim() || '',
        },
      };
    };

    const persistLeadDraft = () => {
      const payload = collectLeadPayload();
      if (
        !payload.contact.first_name
        && !payload.contact.last_name
        && !payload.contact.email
        && !payload.contact.phone
        && !payload.details.course_type
        && !payload.details.message
      ) {
        return;
      }
      upsertDraft({
        type: leadType,
        step: 1,
        payload,
        resumeUrl,
        title: payload.details.course_type_label || payload.details.course_label,
      });
    };

    const draft = getDraftForPage(leadType);
    if (draft?.payload) {
      setNamedField(form, 'first_name', draft.payload.contact?.first_name);
      setNamedField(form, 'last_name', draft.payload.contact?.last_name);
      setNamedField(form, 'email', draft.payload.contact?.email);
      setNamedField(form, 'phone', draft.payload.contact?.phone);
      setNamedField(form, 'message', draft.payload.details?.message);
    }

    let leadTimer = null;
    let turnstileWidget = null;
    const turnstileMount = form.querySelector('.js-turnstile');
    if (turnstileMount && getTurnstileConfig().enabled) {
      mountTurnstile(turnstileMount).then((widget) => {
        turnstileWidget = widget;
      }).catch(() => {
        turnstileWidget = null;
      });
    }
    form.addEventListener('input', () => {
      window.clearTimeout(leadTimer);
      leadTimer = window.setTimeout(persistLeadDraft, 400);
    });
    form.addEventListener('change', persistLeadDraft);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const status = form.querySelector('.js-instructor-lead-status');
      const submit = form.querySelector('.school-instructor-lead__submit');
      const firstName = form.querySelector('[name="first_name"]')?.value?.trim() || '';
      const lastName = form.querySelector('[name="last_name"]')?.value?.trim() || '';
      const email = form.querySelector('[name="email"]')?.value?.trim() || '';
      const phone = form.querySelector('[name="phone"]')?.value?.trim() || '';
      const message = form.querySelector('[name="message"]')?.value?.trim() || '';
      const termsEl = form.querySelector('[name="terms"]');
      const termsOk = termsEl
        ? (termsEl.type === 'checkbox' ? termsEl.checked : termsEl.value === '1')
        : true;
      const courseTypeCombo = form.querySelector('[data-combo="course_type"]');
      const termCombo = form.querySelector('[data-combo="term"]');
      const hasCourseType = Boolean(courseTypeCombo);
      const hasTerm = Boolean(termCombo);
      const courseType = courseTypeCombo?.dataset.value || '';
      const courseTypeLabel = courseTypeCombo?.dataset.label || '';
      const term = termCombo?.dataset.value || '';
      const termLabel = termCombo?.dataset.label || '';
      const dateFrom = termCombo?.dataset.dateFrom || '';
      const dateTo = termCombo?.dataset.dateTo || '';

      const setStatus = (messageText, isError = false) => {
        if (!status) return;
        status.hidden = !messageText;
        status.textContent = messageText;
        status.classList.toggle('is-error', isError);
      };

      if (isContactLead) {
        if (!firstName || !lastName || !email || !phone) {
          setStatus('Uzupełnij imię, nazwisko, e-mail i telefon.', true);
          return;
        }
        if (!message) {
          setStatus('Wpisz wiadomość.', true);
          return;
        }
      } else {
        if (!firstName || !email) {
          setStatus('Uzupełnij imię i adres e-mail.', true);
          return;
        }
        if ((hasCourseType && !courseType) || (hasTerm && !term)) {
          setStatus('Wybierz rodzaj kursu i termin.', true);
          return;
        }
      }
      if (!termsOk) {
        setStatus('Zaakceptuj regulamin.', true);
        return;
      }

      const payload = {
        type: leadType,
        contact: {
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          terms: true,
        },
        details: {
          course_label: courseLabel,
          course_type: courseType,
          course_type_label: courseTypeLabel,
          term: termLabel || term,
          date_label: termLabel || term,
          date_from: dateFrom,
          date_to: dateTo,
          message,
        },
      };

      const restUrl = window.hskiBooking?.restUrl || '/wp-json/hski/v1/booking';
      const restNonce = window.hskiBooking?.restNonce || '';
      const turnstileCfg = getTurnstileConfig();
      if (turnstileCfg.enabled) {
        const token = turnstileWidget?.getToken?.() || '';
        if (!token) {
          turnstileWidget?.reveal?.({ scroll: true });
          setStatus('Potwierdź, że nie jesteś botem — zaznacz weryfikację powyżej.', true);
          return;
        }
        payload['cf-turnstile-response'] = token;
      }
      if (submit) submit.disabled = true;
      setStatus('Wysyłanie…');

      try {
        const response = await fetch(restUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(restNonce ? { 'X-WP-Nonce': restNonce } : {}),
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          turnstileWidget?.reset?.();
          setStatus(data.message || 'Nie udało się wysłać zgłoszenia.', true);
          return;
        }
        const existingDraft = getDraftForPage(leadType);
        if (existingDraft?.id) removeDraft(existingDraft.id);
        form.reset();
        setStatus('');
        if (dialog?.open && showDialogSuccessView()) {
          return;
        }
        if (!form.classList.contains('school-instructor-lead--modal') && showSidebarSuccessView(form)) {
          return;
        }
        setStatus(`Dziękujemy! Numer zgłoszenia: ${data.reference || '—'}`);
        if (dialog?.open) {
          setTimeout(closeDialog, 1800);
        }
      } catch (_err) {
        setStatus('Błąd połączenia. Spróbuj ponownie.', true);
      } finally {
        if (submit) submit.disabled = false;
      }
    });
  });
};

export { LESSON_PRICING, getLessonPrice, buildLessonBookingUrl };
export default initSchoolProduct;
